import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import toast from "react-hot-toast";
import { FaEdit } from "react-icons/fa";
import { format } from "date-fns";
import { useAuthContext } from "../../context/auth";
import { useRRHHCalculations } from "../../pages/ER/hooks/useRRHHCalculations";
import { getActivos, actualizarActivo } from "../../utils/activos";
import { calcularImpuestos } from "../../utils/calcularImpuestos";
import { getFacturasEmitidas, getFacturasRecibidas } from "../../utils/conciliacionSII";
import { getCuotasPendientesProyeccion } from "../../utils/creditos";
import { FECHA_CORTE, construirLedgerProveedor, TOLERANCIA_REDONDEO_LOCAL } from "../../utils/estadoCuenta";
import { getDolarValue, getDolarFallback } from "../../utils/exchangeRateService";
import { getFacturasMoraNeteada, getMorasPendientes, getOperacionesFactoring } from "../../utils/factoring";
import { facturaSiiPorPagar as facturaSiiPorPagarUtil } from "../../utils/facturasPorPagar";
import { getEdificiosSaldoPendiente } from "../../utils/getEdificiosSaldoPendiente";
import { detectarEstadoPago } from "../../utils/iva";
import { getCostosLotesFuturos } from "../../utils/loteCostos";
import { getLotes } from "../../utils/lotesCompra";
import {
  getMovimientosBancarios,
  getCuentasBancarias,
  getFacturasYaVinculadas,
} from "../../utils/movimientosBancarios";
import { getPagosPorMovimientos } from "../../utils/pagosEmpleados";
import { getPipelineCobro } from "../../utils/pipelineCobroSII";
import { getCuotasPagosFuturas, getCuotasCobrosFuturas } from "../../utils/planCobros";
import { getCuotasActivosFuturas, getActivosConPlan } from "../../utils/planPagosActivos";
import { getPlanPagosEdificios } from "../../utils/planPagosEdificios";
import { getPlazosCobroParaSII } from "../../utils/plazosCobroSII";
import { procesarVentaDivisas } from "../../utils/procesarVentaDivisas";
import { getNombreProveedor } from "../../utils/proveedores";
import { getProveedoresRecurrentesEntries } from "../../utils/proveedoresRecurrentes";
import { getSaldosIniciales } from "../../utils/saldosIniciales";
import { client } from "../../utils/supabase";
import { useExchangeRates } from "../../utils/useExchangeRates";
import { Header } from "../Header";
import KebabMenu from "../KebabMenu";
import PlanCuotasModal from "../PlanCuotasModal";
import PlanPagoEdificioModal from "../PlanPagoEdificioModal";
import VincularMovimientoModal from "../VincularMovimientoModal";
import ConfirmarCapexPagadoModal from "./ConfirmarCapexPagadoModal";
import EditarFechaCobroModal from "./EditarFechaCobroModal";
import ModificarFechaProyeccionCapexModal from "./ModificarFechaProyeccionCapexModal";
import OverrideF29Modal from "./OverrideF29Modal";
import PlanPagosActivoModal from "./PlanPagosActivoModal";
import styles from "./FlujoCajaV2.module.scss";

const FILTROS_PROYECCION = [
  { key: "todo", label: "Todo" },
  { key: "cobros", label: "Por cobrar" },
  { key: "backlog", label: "Backlog" },
  { key: "proveedores_recurrentes", label: "Recurrentes" },
  { key: "proveedores_facturados", label: "Por pagar" },
  { key: "edificios", label: "Edificios" },
  { key: "rrhh", label: "RRHH" },
  { key: "impuestos", label: "Impuestos" },
  { key: "capex", label: "CapEx" },
  { key: "financiamiento", label: "Pasivo" },
];

const FILTROS_HISTORICO = [
  { key: "todo", label: "Todo" },
  { key: "ingresos", label: "Ingresos" },
  { key: "egresos", label: "Egresos" },
];

// Cuatro slots de corte con labels fijos: "Mes" (fin del mes actual), "+1", "+2",
// "Año" (31/12 del año en curso). Las fechas son dinámicas; los labels no.
const calcularFiltrosTemporales = () => {
  const hoy = new Date();
  const yyyy = hoy.getFullYear();
  const m0 = hoy.getMonth();
  const finDeMes = (y, m) => new Date(y, m + 1, 0);
  const toKey = (fecha) => {
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, "0");
    const d = String(fecha.getDate()).padStart(2, "0");
    return `corte_${y}-${m}-${d}`;
  };
  const slots = [
    { label: "Mes", fechaCorte: finDeMes(yyyy, m0) },
    { label: "+1", fechaCorte: finDeMes(yyyy, m0 + 1) },
    { label: "+2", fechaCorte: finDeMes(yyyy, m0 + 2) },
    { label: "Año", fechaCorte: new Date(yyyy, 11, 31) },
  ];
  return slots.map((s) => ({ ...s, key: toKey(s.fechaCorte) }));
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  let f = fechaStr;
  if (f.includes("T")) f = f.substring(0, 10);
  const [, month, day] = f.split("-");
  return `${day}/${month}`;
};

// Fecha LOCAL en "YYYY-MM-DD" (getFullYear/getMonth/getDate), NO UTC. Clave para los rangos de fecha
// del histórico: toISOString() serializa en UTC y de tarde/noche en Chile/Perú (UTC−4/−5) apunta al
// día SIGUIENTE, dejando "Hoy" sin matchear los movimientos de la fecha local. Ancla a la zona de la
// máquina del usuario (correcto mientras esté en la zona del país operativo, el caso normal).
const toLocalYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// ─── Badge de estado del movimiento bancario ────────────────────────────
// Sistema consistente: cada movimiento muestra un badge según su tipo de vinculación.
// P=pendiente, E=empleado, V=venta (SII emitida), C=compra SII (recibida),
// X=compra extranjera (factura externa, rombo), I=ignorado.
const BADGE_BASE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.65rem",
  fontWeight: 700,
  width: 22,
  height: 22,
  borderRadius: 4,
  verticalAlign: "middle",
};

// Tooltip estructurado: tipo como encabezado, detalles abajo
// Formato de monto compacto para tooltips (renderBadgeMovimiento es módulo, sin acceso al hook useCurrency).
const fmtMontoTt = (n, moneda) => {
  const pref = moneda === "USD" ? "US$ " : moneda === "PEN" ? "S/ " : "$";
  return pref + Number(n || 0).toLocaleString("es-CL", { maximumFractionDigits: moneda === "USD" ? 2 : 0 });
};

const buildTooltip = ({ tipo, folioLabel, folio, entidad }) => (
  <div style={{ textAlign: "left", padding: "2px 4px", minWidth: 140, maxWidth: 260 }}>
    <div
      style={{
        fontSize: "0.65rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        opacity: 0.75,
        marginBottom: 3,
      }}
    >
      {tipo}
    </div>
    {folio && (
      <div style={{ fontSize: "0.78rem", fontWeight: 600, fontFamily: "monospace", marginBottom: entidad ? 2 : 0 }}>
        {folioLabel} {folio}
      </div>
    )}
    {entidad && <div style={{ fontSize: "0.72rem", opacity: 0.9, lineHeight: 1.25 }}>{entidad}</div>}
  </div>
);

const BadgeConTooltip = ({ tooltip, tooltipId, children }) => (
  <OverlayTrigger
    placement="top"
    delay={{ show: 100, hide: 100 }}
    overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}
  >
    {children}
  </OverlayTrigger>
);

const renderBadgeMovimiento = (
  m,
  pagosMovIds,
  enteFiscal = "SII",
  factoringMovIds = new Set(),
  factoringMap = new Map(),
  facturaInfoMap = new Map(),
  facturaRecibidaInfoMap = new Map()
) => {
  // Vinculado a operación de factoring (adelanto o excedente) → F violeta. Prioritario
  // sobre cualquier otro check para que se vea aunque también caiga en algún match secundario.
  if (factoringMovIds.has(m.id)) {
    const entries = factoringMap.get(m.id) || [];
    const provNombre = entries[0]?.op?.proveedor?.nombre || "Factoring";
    const tiposUnicos = [...new Set(entries.map((e) => e.tipoVinc))];
    const tipoLabel = tiposUnicos.length === 1 ? tiposUnicos[0] : "Múltiple";
    const fmtOpts = { style: "currency", currency: m.moneda || "CLP", maximumFractionDigits: 0 };
    const fmtMonto = (n) => Number(n || 0).toLocaleString("es-CL", fmtOpts);

    // 1 op → tooltip clásico vía buildTooltip. N ops → JSX inline con lista por folio.
    let tooltipContent;
    if (entries.length === 1) {
      const e = entries[0];
      const montoVinc = e.tipoVinc === "Giro" ? Number(e.op.giro_a_recibir || 0) : Number(e.op.monto_remanente || 0);
      tooltipContent = buildTooltip({
        tipo: `Factoring · ${e.tipoVinc}`,
        entidad: provNombre,
        folio: montoVinc > 0 ? fmtMonto(montoVinc) : null,
        folioLabel: "Monto",
      });
    } else {
      tooltipContent = (
        <div style={{ textAlign: "left", padding: "2px 4px", minWidth: 180, maxWidth: 280 }}>
          <div
            style={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              opacity: 0.75,
              marginBottom: 3,
            }}
          >
            {`Factoring · ${tipoLabel} (${entries.length} ops)`}
          </div>
          <div style={{ fontSize: "0.72rem", opacity: 0.9, lineHeight: 1.25, marginBottom: 4 }}>{provNombre}</div>
          {entries.map((e, i) => {
            const monto = e.tipoVinc === "Giro" ? Number(e.op.giro_a_recibir || 0) : Number(e.op.monto_remanente || 0);
            return (
              <div key={i} style={{ fontSize: "0.72rem", fontFamily: "monospace", lineHeight: 1.3 }}>
                Folio {e.op.factura?.folio || "—"}: {fmtMonto(monto)}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <BadgeConTooltip tooltip={tooltipContent} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#922b21" }}>F</span>
      </BadgeConTooltip>
    );
  }
  // Pago a empleado → E (chequeo externo, depende del state pagosEmpleadosRegistrados)
  if (pagosMovIds.has(m.id)) {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Pago a empleado" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#27ae60" }}>E</span>
      </BadgeConTooltip>
    );
  }
  // Ignorado → I gris (estado, prioridad sobre cualquier categoría)
  if (m.estado === "ignorado") {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Ignorado" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#707b7c" }}>I</span>
      </BadgeConTooltip>
    );
  }
  // Resto de badges: derivados de categoria_pago en movimientos_caja (fuente semántica).
  // Esto cubre tanto single (FK directa poblada) como múltiple (FK directa NULL pero
  // movimiento_bancario_facturas tiene N filas) — el badge depende del tipo contable, no del
  // shape de la vinculación.
  // movimientos_caja.movimiento_bancario_id tiene UNIQUE → PostgREST devuelve el embed como objeto.
  const categoriaCaja = m.movimiento_caja?.categoria_pago;

  // Cobro SII (single o múltiple) → V
  if (categoriaCaja === "cobro_factura") {
    // Single: la info (tipo_dte, folio, razon_social_receptor) viene del embed
    // factura_emitida. Multi: factura_emitida queda null al confirmar múltiple →
    // resolver vía facturaInfoMap construido en cliente desde facturas_vinculadas
    // (movimiento_bancario_facturas no tiene FK formal a facturas_sii_emitidas, así
    // que PostgREST no puede embeber el anidado).
    const vincsEmitidas = !m.factura_emitida
      ? (m.facturas_vinculadas || [])
          .filter((v) => v.factura_tipo === "sii_emitida")
          .map((v) => facturaInfoMap.get(v.factura_id))
          .filter(Boolean)
      : [];
    const esCobroExportacion =
      m.factura_emitida?.tipo_dte === 110 || vincsEmitidas.some((info) => info.tipo_dte === 110);
    const foliosMulti = vincsEmitidas
      .map((i) => i.folio)
      .filter(Boolean)
      .sort((a, b) => a - b); // orden ascendente determinístico ("35, 36"), no orden de inserción MBF
    const tooltipFolio = m.factura_emitida?.folio ?? (foliosMulti.length > 0 ? foliosMulti.join(", ") : null);
    const tooltipFolioLabel = tooltipFolio ? (foliosMulti.length > 1 ? "Folios" : "Folio") : null;
    const tooltipEntidad = m.factura_emitida?.razon_social_receptor ?? vincsEmitidas[0]?.razon_social_receptor;
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: esCobroExportacion ? "Cobro Exportación" : `Cobro ${enteFiscal}`,
          folioLabel: tooltipFolioLabel,
          folio: tooltipFolio,
          entidad: tooltipEntidad,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#2980b9" }}>V</span>
      </BadgeConTooltip>
    );
  }
  // Compra extranjera (factura externa) → X. Cae antes que C porque comparte
  // categoria_pago='pago_proveedor' pero se distingue por match_factura_tipo.
  if (m.match_factura_tipo === "factura_externa") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: "Compra extranjera",
          folioLabel: "Doc",
          folio: m.factura_externa?.[0]?.numero_documento,
          entidad: m.match_entidad_nombre,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#117a8b" }}>X</span>
      </BadgeConTooltip>
    );
  }
  // Compra SII (single o múltiple) → C
  if (categoriaCaja === "pago_proveedor") {
    let tooltipC;
    if (m.factura_recibida) {
      // Single: folio + emisor del embed factura_recibida (FK directa) → tooltip clásico.
      tooltipC = buildTooltip({
        tipo: `Compra ${enteFiscal}`,
        folioLabel: "Folio",
        folio: m.factura_recibida.folio,
        entidad: m.factura_recibida.razon_social_emisor,
      });
    } else {
      // Multi (FK null): la info se resuelve vía facturaRecibidaInfoMap (poblado desde
      // facturas_vinculadas). Lista UNA factura por línea (folio + razón social) + la propina si el
      // débito supera la Σ de facturas (excedente sin DTE, 10.108). Orden asc determinístico de folio.
      const vincsRecibidas = (m.facturas_vinculadas || [])
        .filter((v) => v.factura_tipo === "sii_recibida")
        .map((v) => facturaRecibidaInfoMap.get(v.factura_id))
        .filter(Boolean)
        .sort((a, b) => Number(a.folio) - Number(b.folio));
      const sumFacturas = vincsRecibidas.reduce((s, i) => s + Number(i.monto_total || 0), 0);
      const propinaC = Math.abs(Number(m.monto || 0)) - sumFacturas;
      const tienePropina = vincsRecibidas.length > 0 && propinaC > 0.5;
      tooltipC = (
        <div style={{ textAlign: "left", padding: "2px 4px", minWidth: 160, maxWidth: 300 }}>
          <div
            style={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              opacity: 0.75,
              marginBottom: 3,
            }}
          >
            {`Compra ${enteFiscal}${vincsRecibidas.length > 1 ? ` (${vincsRecibidas.length} facturas)` : ""}`}
          </div>
          {vincsRecibidas.map((i, idx) => (
            <div key={idx} style={{ fontSize: "0.72rem", lineHeight: 1.3, marginBottom: 1 }}>
              <span style={{ fontFamily: "monospace", fontWeight: 600 }}>Folio {i.folio}</span>
              {i.razon_social_emisor ? ` · ${i.razon_social_emisor}` : ""}
            </div>
          ))}
          {tienePropina && (
            <div style={{ fontSize: "0.72rem", lineHeight: 1.3, opacity: 0.9, marginTop: 3 }}>
              Propina (sin DTE): {fmtMontoTt(propinaC, m.moneda)}
            </div>
          )}
        </div>
      );
    }
    return (
      <BadgeConTooltip tooltip={tooltipC} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#7d3c98" }}>C</span>
      </BadgeConTooltip>
    );
  }
  // Pago institucional → I azul oscuro. PREVIRED, SII (declaración), banco, etc.
  if (
    categoriaCaja === "cotizaciones_previsionales" ||
    categoriaCaja === "pago_impuesto" ||
    categoriaCaja === "pago_banco"
  ) {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Pago institucional" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#1a3a5c" }}>P</span>
      </BadgeConTooltip>
    );
  }
  // Cambio de moneda (Venta/Compra Divisas) → D azul. categoria_pago='costo_financiero'.
  if (categoriaCaja === "costo_financiero") {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Cambio de Moneda" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#1a5276" }}>D</span>
      </BadgeConTooltip>
    );
  }
  // Gasto sin factura → G
  if (categoriaCaja === "gasto_sin_factura") {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Gasto sin factura" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#1e8449" }}>G</span>
      </BadgeConTooltip>
    );
  }
  // Pago a cuenta proveedor → A. Movimiento de cuenta corriente sin factura asociada
  // (anticipos, retiros, devoluciones). entidad_tipo='proveedor', cuenta_er=null.
  // Incluye los buckets nuevos pago_factura/pago_credito del modal (mismo badge visual).
  if (categoriaCaja === "pago_a_cuenta" || categoriaCaja === "pago_factura" || categoriaCaja === "pago_credito") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: "Pago a cuenta",
          entidad: m.movimiento_caja?.entidad_nombre || m.match_entidad_nombre,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#d35400" }}>A</span>
      </BadgeConTooltip>
    );
  }
  // Cobro a cuenta proveedor → A. Ingreso a cuenta corriente del proveedor (devolución,
  // anticipo retornado, refacturación). entidad_tipo='proveedor', cuenta_er=null.
  // Incluye el bucket nuevo cobro_credito del modal (mismo badge visual).
  if (categoriaCaja === "cobro_a_cuenta" || categoriaCaja === "cobro_credito") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: "Cobro a cuenta",
          entidad: m.movimiento_caja?.entidad_nombre || m.match_entidad_nombre,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#d35400" }}>A</span>
      </BadgeConTooltip>
    );
  }
  // Caja chica → CC violeta. Entrega de fondos a un empleado (categoria_pago='caja_chica').
  if (categoriaCaja === "caja_chica") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({ tipo: "Caja Chica", entidad: m.movimiento_caja?.entidad_nombre })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#8e44ad" }}>CC</span>
      </BadgeConTooltip>
    );
  }
  // Pago a edificio → B turquesa. categoria_pago='comision_edificio'. entidad_tipo='edificio'.
  if (categoriaCaja === "comision_edificio") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: "Pago a edificio",
          entidad: m.movimiento_caja?.entidad_nombre || "",
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#1abc9c" }}>B</span>
      </BadgeConTooltip>
    );
  }
  // Fallback: tiene mov_caja_id pero categoria_pago no matcheó (raro). Default G.
  if (m.movimiento_caja_id) {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Gasto sin factura" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#1e8449" }}>G</span>
      </BadgeConTooltip>
    );
  }
  // Pendiente → guion (la fila se tinta naranja sutil vía isMovPendiente)
  return (
    <BadgeConTooltip tooltip={buildTooltip({ tipo: "Pendiente de vincular" })} tooltipId={`tt-${m.id}`}>
      <span
        style={{ color: "#999", fontWeight: 400, display: "inline-block", width: 22, height: 22, textAlign: "center" }}
      >
        —
      </span>
    </BadgeConTooltip>
  );
};

// Marca "forzado/pendiente" (10.108 invertida): si el movimiento tiene una vinculación forzada por
// faltante con estado='pendiente' (tabla vinculaciones_forzadas), devuelve un ⚠ naranja con su
// tooltip para anteponer AL CONCEPTO en el histórico. estado='resuelto' (llegó la NC real) → null.
// Va en la celda de descripción, NO en la del badge: así el badge (la letra) queda en su posición y
// tamaño de siempre, esté o no la marca (la columna del badge se ve idéntica a las filas sin forzar).
// Presentación de texto forzada ("⚠︎") para que el color naranja del CSS se respete (con la
// presentación emoji por defecto el glyph ignoraba el color y quedaba invisible).
const renderMarcaForzado = (m, forzadosMap = new Map()) => {
  const forzado = forzadosMap.get(m.id);
  if (!forzado || forzado.estado !== "pendiente") return null;
  const tooltipForzado = (
    <div style={{ textAlign: "left", padding: "2px 4px", minWidth: 160, maxWidth: 300 }}>
      <div
        style={{
          fontSize: "0.65rem",
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          opacity: 0.75,
          marginBottom: 3,
        }}
      >
        Vinculación forzada
      </div>
      <div style={{ fontSize: "0.72rem", lineHeight: 1.3, opacity: 0.9 }}>
        Faltante forzado (pendiente): {fmtMontoTt(forzado.faltante, m.moneda)}
      </div>
    </div>
  );
  // Posición ABSOLUTA (fuera de flujo) → no aporta altura: la fila mide exactamente igual con o sin
  // marca, por grande que sea el glyph (además el td tiene overflow:hidden). Anclada al extremo
  // derecho de la celda de descripción (la celda es position:relative), centrada verticalmente, con
  // aire respecto a la columna de MONTO (right:10). Presentación de texto (⚠︎ U+FE0E) + naranja + bold.
  return (
    <BadgeConTooltip tooltip={tooltipForzado} tooltipId={`tt-forz-${m.id}`}>
      <span
        style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#e67e22",
          fontWeight: 700,
          fontSize: 17,
          lineHeight: 1,
          cursor: "default",
        }}
      >
        {"⚠︎"}
      </span>
    </BadgeConTooltip>
  );
};

// Detector de pendiente: refleja el fallthrough de renderBadgeMovimiento.
// Se usa para tintar la fila completa, no solo el badge.
const isMovPendiente = (m, pagosMovIds, factoringMovIds = new Set()) => {
  if (factoringMovIds.has(m.id)) return false;
  if (pagosMovIds.has(m.id)) return false;
  if (m.estado === "ignorado") return false;
  const cat = m.movimiento_caja?.categoria_pago;
  if (cat === "cobro_factura") return false;
  if (m.match_factura_tipo === "factura_externa") return false;
  if (cat === "pago_proveedor") return false;
  if (cat === "cotizaciones_previsionales" || cat === "pago_impuesto" || cat === "pago_banco") return false;
  if (cat === "gasto_sin_factura") return false;
  if (cat === "pago_a_cuenta") return false;
  if (cat === "cobro_a_cuenta") return false;
  if (cat === "costo_financiero") return false;
  if (cat === "caja_chica") return false;
  if (cat === "comision_edificio") return false;
  if (m.movimiento_caja_id) return false;
  return true;
};

// La columna EDIT del panel futuros es editable solo para entries que resuelven a una fila real
// (factura SII, externa, edificio, pipeline). Las sintéticas recurrente/residuo NO son editables.
// `es_lote` queda fuera: una línea de LOTE se ejecuta desde la pantalla Activos (genera
// las N unidades), no con el lápiz de "marcar pagado" — ese handler espera un activo_id
// y con un lote haría un return silencioso. Se habilita en la sub-fase 5 del bloque Stock.
const esEntryEditable = (f) =>
  ["facturado", "proveedor", "edificio", "pipeline", "capex"].includes(f.tipo) &&
  !f.es_recurrente &&
  !f.es_residuo &&
  !f.es_lote;

// Período tributario de una fila de impuesto, derivado de fecha_display (día 12 del mes SIGUIENTE
// al período → mes − 1), para no depender del texto del label. Lo usa el override de PPM/IUT.
const periodoDeImpuesto = (f) => {
  if (!f || f.tipo !== "impuesto" || !f.fecha_display) return null;
  const [yy, mm] = f.fecha_display.split("-").map(Number);
  if (!yy || !mm) return null;
  const py = mm === 1 ? yy - 1 : yy;
  const pm = mm === 1 ? 12 : mm - 1;
  return `${py}-${String(pm).padStart(2, "0")}`;
};

// RUT de la empresa por país (single-tenant). Solo Chile tiene bloque F29 / override. Coincide
// con RUT_BASE de los edge functions (Merinell 76857932). Comparación/escritura por ID.
const RUT_EMPRESA_POR_PAIS = { Chile: "76857932" };
// PPM/IUT de un mes CERRADO (id `ppm-YYYY-MM` / `iut-YYYY-MM`, NO los estimados `*-est-*`) →
// se pueden overridear a mano con el valor del contador.
const esFilaOverrideF29 = (f) => f && f.tipo === "impuesto" && /^(ppm|iut)-\d{4}-\d{2}$/.test(f.id || "");

// Invoca un edge function de sync (POST, sin body) con el anon key. Tira si devuelve {error}.
// Lo usan f29-sync / honorarios-sync / rcv-sync, enganchados al botón "Actualizar SII".
const invocarEdgeSync = async (nombre) => {
  const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${nombre}`;
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
    },
  });
  const data = await resp.json();
  if (data.error) throw new Error(data.error);
  return data;
};

const FlujoCajaV2 = () => {
  const { pais, id: userId } = useAuthContext();
  const { loaded: ratesLoaded } = useExchangeRates(pais);
  const monedaLocal = pais === "Chile" ? "CLP" : "PEN";
  const simboloLocal = pais === "Chile" ? "$" : "S/";
  const enteFiscal = pais === "Chile" ? "SII" : "SUNAT";
  const [monedaVista, setMonedaVista] = useState(monedaLocal);
  const [consolidado, setConsolidado] = useState(false);
  const [convertirAUSD, setConvertirAUSD] = useState(false);
  const [filtrosActivos, setFiltrosActivos] = useState(["todo"]);
  // Sin filtro temporal por defecto (null) → la vista muestra hasta el 31/12 del año
  // siguiente (techo por defecto en filtrarPorCorte). Los botones Mes/+1/+2/Año son
  // cortes opcionales que se activan y se pueden desactivar (toggle).
  const [filtroTemporalKey, setFiltroTemporalKey] = useState(null);
  const [filtroHistorico, setFiltroHistorico] = useState("todo");
  // Toggle independiente y combinable: filtra a solo movs pendientes de vincular
  // (predicado isMovPendiente, fuente única). Se suma a filtroHistorico + filtroTiempo.
  const [soloPendientes, setSoloPendientes] = useState(false);
  // Toggle independiente (⚠): filtra a solo movs con forzado pendiente (forzadosMap). Se apila
  // sobre fecha + tipo + búsqueda. Enabled solo si hay ⚠ en la vista base (hayAtencionEnVista).
  const [soloAtencion, setSoloAtencion] = useState(false);
  const [filtroTiempo, setFiltroTiempo] = useState("ytd");
  const [busquedaHistorico, setBusquedaHistorico] = useState("");
  const [busquedaFuturos, setBusquedaFuturos] = useState("");

  const toggleFiltro = (filtro) => {
    if (filtro === "todo") {
      setFiltrosActivos(["todo"]);
      return;
    }
    setFiltrosActivos((prev) => {
      const sinTodo = prev.filter((f) => f !== "todo");
      if (sinTodo.includes(filtro)) {
        const nuevo = sinTodo.filter((f) => f !== filtro);
        return nuevo.length === 0 ? ["todo"] : nuevo;
      }
      return [...sinTodo, filtro];
    });
  };

  const [movBancarios, setMovBancarios] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [saldosIniciales, setSaldosIniciales] = useState([]);
  const [pendientesCobro, setPendientesCobro] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [proveedoresPendientes, setProveedoresPendientes] = useState([]);
  const [proveedoresRecurrentes, setProveedoresRecurrentes] = useState([]);
  const [impuestosEntries, setImpuestosEntries] = useState([]);
  const [capexEntries, setCapexEntries] = useState([]);
  // Lotes de compra planificados (bloque Stock) — segunda fuente del tab CapEx.
  const [capexLotes, setCapexLotes] = useState([]);
  // Componentes de costo (landed cost) pendientes — tercera fuente del tab CapEx. SUMAN a la línea
  // FOB del lote (no la reemplazan): el FOB es la base, estos son extras que capitalizan aparte (9.36).
  const [componentesLotes, setComponentesLotes] = useState([]);
  const [edificiosMap, setEdificiosMap] = useState({});
  const [edificiosSaldo, setEdificiosSaldo] = useState([]);
  const [planPagosEdificios, setPlanPagosEdificios] = useState([]);
  const [pagoEdificioModal, setPagoEdificioModal] = useState(null);
  const [morasPendientes, setMorasPendientes] = useState([]);
  const [cuotasCreditos, setCuotasCreditos] = useState([]);
  const [operacionesFactoring, setOperacionesFactoring] = useState([]);
  // IDs de movimientos bancarios que ya están vinculados como adelanto o remanente de
  // alguna operación de factoring. Se calcula a partir de operacionesFactoring para no
  // hacer un fetch separado.
  const [movimientosBancarioFactoring, setMovimientosBancarioFactoring] = useState(() => new Set());
  const [mapaFactoring, setMapaFactoring] = useState(() => new Map());
  const [facturaInfoMap, setFacturaInfoMap] = useState(() => new Map());
  // Espejo del anterior para facturas RECIBIDAS vinculadas vía multi (MBF, FK directa null): el
  // badge C resuelve folio + razón social del emisor desde acá cuando m.factura_recibida es null.
  const [facturaRecibidaInfoMap, setFacturaRecibidaInfoMap] = useState(() => new Map());
  // Vinculaciones forzadas por faltante (10.108 invertida): movimiento_bancario_id → { estado, faltante }.
  // Alimenta la marca "forzado/pendiente" del histórico. Arranca vacío → sin cambio visual hasta Fase 3.
  const [forzadosMap, setForzadosMap] = useState(() => new Map());
  const [planCuotasModal, setPlanCuotasModal] = useState(null);
  const [vincularModal, setVincularModal] = useState(null);
  const [entryEditarFecha, setEntryEditarFecha] = useState(null);
  const [capexPagadoModal, setCapexPagadoModal] = useState(null);
  const [capexFechaModal, setCapexFechaModal] = useState(null);
  const [capexPlanModal, setCapexPlanModal] = useState(null);
  // { entry, campo: 'ppm'|'iut', periodo } — carga manual del PPM/IUT del contador (bloque F29).
  const [overrideF29Modal, setOverrideF29Modal] = useState(null);
  const [cuotasPagosPendientes, setCuotasPagosPendientes] = useState([]);
  const [cuotasCobrosPendientes, setCuotasCobrosPendientes] = useState([]);
  // Plan de pagos de activos CapEx: cuotas pendientes (proyección por cuota) + set de activos que
  // tienen plan (para no volver a la línea única cuando el plan está completo).
  const [cuotasActivosPendientes, setCuotasActivosPendientes] = useState([]);
  const [activosConPlan, setActivosConPlan] = useState(() => new Set());
  // IDs de facturas SII recibidas vinculadas a caja chica de algún empleado — se filtran de
  // "Por pagar" (su pago se rinde contra el fondo del empleado, no es deuda a proveedor).
  const [cajaChicaFacturaIds, setCajaChicaFacturaIds] = useState(() => new Set());
  // IDs de facturas SII recibidas de comisión NETEADA de un ingreso — unión de dos fuentes:
  // comisión de factoring (operaciones_factoring_comisiones) y comisión de recepción de
  // transferencia internacional (comisiones_ingreso_neteadas). Ninguna genera débito → se
  // filtran de "Por pagar" y del ledger.
  const [facturasComisionFactoringSet, setFacturasComisionFactoringSet] = useState(() => new Set());
  // IDs de facturas SII recibidas registradas como mora saldada por NETEO (operaciones_factoring_mora,
  // estado='saldada_neteo'). El DTE de mora se descontó del giro de una op posterior, sin débito
  // bancario → el FIFO las deja vivas; se excluyen de "Por pagar" con un gate propio en el predicado.
  const [facturasMoraNeteadaSet, setFacturasMoraNeteadaSet] = useState(() => new Set());
  const [yaVinculadasExternas, setYaVinculadasExternas] = useState(() => new Set());
  // Set de emitidas ya cobradas por CUALQUIER vía bancaria (FK directa + cuotas completas +
  // MBF con Σ ≥ total). Lo construye getFacturasYaVinculadas; "Por cobrar" lo usa para excluir.
  const [yaVinculadasEmitidas, setYaVinculadasEmitidas] = useState(() => new Set());
  const [facturasExternasPendientes, setFacturasExternasPendientes] = useState([]);
  // Saldo neto por proveedor (SII + externas + movs caja). El panel "Por pagar"
  // muestra detalle por folio/cuota, no el saldo consolidado — el agregado vive arriba
  // (ResumenSaldo). State preservado intencionalmente para consumidores futuros / ledger.
  // eslint-disable-next-line no-unused-vars
  const [saldosPendientesProveedores, setSaldosPendientesProveedores] = useState([]);
  // Map<"origen:id", saldoRestanteNativo> — saldo restante por factura calculado vía FIFO
  // aplicando pagos cronológicamente sobre facturas pendientes del mismo proveedor. Cubre
  // el caso donde los pagos no están vinculados a una factura específica vía FK. "origen"
  // ∈ {SII, EXT, LEG}. Una factura ausente del map = totalmente pagada (saldo ≤ 0.01).
  const [saldosPorFacturaMap, setSaldosPorFacturaMap] = useState(() => new Map());
  // Moneda en que está expresado cada saldo del ledger por proveedor (provId → provMoneda,
  // = moneda_default || monedaLocal). El consumidor en proyeccionesFuturas la usa para
  // re-expresar el saldo a la moneda de display de forma consistente, en vez de asumir
  // que coincide con la moneda de la factura (que puede diferir si el dato es inconsistente).
  const [provMonedaMap, setProvMonedaMap] = useState(() => new Map());
  // Residuos por proveedor: deuda global del ledger que NO se atribuye a una factura
  // específica (cobros a cuenta, saldo inicial). Aparece como fila "Saldo a cuenta"
  // en "Por pagar" para que la suma del panel coincida con el saldo del estado de
  // cuenta. Shape: [{ provId, nombre, saldoDisplay, moneda }].
  const [residuosProveedoresPendientes, setResiduosProveedoresPendientes] = useState([]);
  const [pagosEmpleadosRegistrados, setPagosEmpleadosRegistrados] = useState([]);
  // Pagos de PREVIRED (movimientos_caja categoria='cotizaciones_previsionales') para conciliar el
  // PREVIRED del tab RRHH a nivel mes (R2). Se puebla en cargarDatosFuturos desde movimientosCajaRes.
  const [movimientosCajaCotizaciones, setMovimientosCajaCotizaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sincronizandoBanco, setSincronizandoBanco] = useState(false);
  const [sincronizandoSII, setSincronizandoSII] = useState(false);
  const [ultimaConsulta, setUltimaConsulta] = useState(null);

  const currentYear = new Date().getFullYear();
  const { empleadosRRHH, isEmpleadosRRHHLoading, getSueldoBaseVigenteParaAno } = useRRHHCalculations(
    pais,
    currentYear,
    12,
    [],
    []
  );

  const pagosMovIds = useMemo(
    () => new Set(pagosEmpleadosRegistrados.map((p) => p.movimiento_bancario_id).filter(Boolean)),
    [pagosEmpleadosRegistrados]
  );

  const pagadoLiquidoPorEmpMes = useMemo(() => {
    const map = {};
    for (const p of pagosEmpleadosRegistrados) {
      if (p.tipo !== "sueldo" && p.tipo !== "adelanto") continue;
      if (!p.empleado_id || !p.periodo_año || !p.periodo_mes) continue;
      const key = `${p.empleado_id}-${p.periodo_año}-${p.periodo_mes}`;
      map[key] = (map[key] || 0) + Number(p.monto || 0);
    }
    return map;
  }, [pagosEmpleadosRegistrados]);

  const rrhhEntries = useMemo(() => {
    if (isEmpleadosRRHHLoading || !empleadosRRHH?.length) return [];
    const hoy = new Date().toISOString().split("T")[0];
    const añoBase = new Date().getFullYear();

    const DESCUENTO_EMPLEADO_CL = 0.1827;
    const CARGA_EMPLEADOR_CL = 0.052;
    const DESCUENTO_EMPLEADO_PE = 0.13;
    const CARGA_EMPLEADOR_PE = 0.09;

    const entries = [];

    // Proyectar sueldos/cargas desde enero del año actual hasta diciembre del año
    // siguiente, para cubrir la vista completa (sin corte temporal) del panel futuro.
    for (const año of [añoBase, añoBase + 1]) {
      for (let mesIdx = 0; mesIdx < 12; mesIdx++) {
        const ultimoDiaMes = new Date(año, mesIdx + 1, 0).toISOString().split("T")[0];
        const mesSiguiente = mesIdx === 11 ? 1 : mesIdx + 2;
        const añoSiguiente = mesIdx === 11 ? año + 1 : año;
        const dia12SiguienteMes = `${añoSiguiente}-${String(mesSiguiente).padStart(2, "0")}-12`;
        const periodoMes = `${año}-${String(mesIdx + 1).padStart(2, "0")}`;

        // Corte: no proyectar períodos < 2026-01 (saldados por saldo inicial). Reemplaza el viejo
        // guard que salteaba TODO mes cerrado por fecha (R1, regla 10.163): ahora los meses cerrados
        // pasan y el líquido impago se genera; PREVIRED y Perú mantienen su propio guard de fecha.
        if (periodoMes < "2026-01") continue;

        // R2: PREVIRED consolidado por mes (el pago real es UNO por mes, todos los empleados juntos).
        // Se acumula durante el loop de empleados y se emite UNA fila después.
        let previredMesChile = 0;

        for (const emp of empleadosRRHH) {
          const mesStr = `${año}-${String(mesIdx + 1).padStart(2, "0")}-01`;
          if (emp.egreso_fecha && emp.egreso_fecha < mesStr) continue;

          const bruto = getSueldoBaseVigenteParaAno(emp, mesIdx, año) || 0;
          if (bruto <= 0) continue;

          const nombreRaw = `${emp.nombre || ""} ${emp.apellido || ""}`.trim();
          const nombre = nombreRaw
            .toLowerCase()
            .split(" ")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");

          const yaPagado = pagadoLiquidoPorEmpMes[`${emp.id}-${año}-${mesIdx + 1}`] || 0;

          if (pais === "Chile") {
            const liquidoEsperado = Math.round(bruto * (1 - DESCUENTO_EMPLEADO_CL));
            const saldoLiquido = Math.max(0, liquidoEsperado - yaPagado);
            const previred = Math.round(bruto * (DESCUENTO_EMPLEADO_CL + CARGA_EMPLEADOR_CL));

            // R1 (regla 10.163): el líquido sale de la proyección SOLO cuando está pagado de verdad
            // (saldoLiquido = 0 vía yaPagado bank-backed), NO por fecha. Vencido impago queda visible
            // (fecha pasada → rojo por la regla universal del render).
            if (saldoLiquido > 0) {
              entries.push({
                id: `rrhh-liquido-${emp.id}-${año}-${mesIdx}`,
                fecha_display: ultimoDiaMes,
                importeEnMoneda: -saldoLiquido,
                razon_social_receptor: `RRHH | ${nombre}${yaPagado > 0 ? " (saldo)" : ""}`,
                tipo: "rrhh",
              });
            }

            // PREVIRED consolidado: acumular (la fila única del mes se emite tras el loop de empleados).
            previredMesChile += previred;
          } else {
            const liquidoEsperado = Math.round(bruto * (1 - DESCUENTO_EMPLEADO_PE));
            const saldoLiquido = Math.max(0, liquidoEsperado - yaPagado);
            const essalud = Math.round(bruto * CARGA_EMPLEADOR_PE);

            if (ultimoDiaMes > hoy && saldoLiquido > 0) {
              entries.push({
                id: `rrhh-liquido-${emp.id}-${año}-${mesIdx}`,
                fecha_display: ultimoDiaMes,
                importeEnMoneda: -saldoLiquido,
                razon_social_receptor: `RRHH | ${nombre}${yaPagado > 0 ? " (saldo)" : ""}`,
                tipo: "rrhh",
              });
            }
            if (ultimoDiaMes > hoy) {
              entries.push({
                id: `rrhh-essalud-${emp.id}-${año}-${mesIdx}`,
                fecha_display: dia12SiguienteMes,
                importeEnMoneda: -essalud,
                razon_social_receptor: `EsSalud | ${nombre}`,
                tipo: "rrhh",
              });
            }
          }
        }

        // R2 (regla 10.163): PREVIRED consolidado sale de la proyección SOLO si hay pago real
        // conciliado a nivel mes (movimientos_caja categoria='cotizaciones_previsionales', match por
        // período con el mismo patrón de detectarEstadoPago). NO por fecha: vencido impago queda
        // (fecha pasada → rojo por la regla universal). Solo Chile.
        if (pais === "Chile" && previredMesChile > 0) {
          const estadoPrevired = detectarEstadoPago(
            periodoMes,
            dia12SiguienteMes,
            movimientosCajaCotizaciones,
            "cotizaciones_previsionales"
          ).estado;
          if (estadoPrevired !== "pagado") {
            entries.push({
              id: `rrhh-previred-${año}-${mesIdx}`,
              fecha_display: dia12SiguienteMes,
              importeEnMoneda: -previredMesChile,
              razon_social_receptor: `PREVIRED ${periodoMes}`,
              tipo: "rrhh",
            });
          }
        }
      }
    }

    return entries.sort((a, b) => a.fecha_display.localeCompare(b.fecha_display));
  }, [
    empleadosRRHH,
    isEmpleadosRRHHLoading,
    pais,
    getSueldoBaseVigenteParaAno,
    pagadoLiquidoPorEmpMes,
    movimientosCajaCotizaciones,
  ]);

  const cargarDatosHistoricos = useCallback(async () => {
    if (!pais || pais === "Region" || !ratesLoaded) return;
    const [movs, ctas, saldosInicialesData] = await Promise.all([
      getMovimientosBancarios(pais, { fechaDesde: "2026-01-01" }),
      getCuentasBancarias(pais),
      client.from("saldos_iniciales_cuentas").select("*"),
    ]);

    // Procesar ventas de divisas antes de setear el state
    await procesarVentaDivisas({
      movimientos: movs,
      cuentas: ctas,
      pais,
    });

    // Segundo fetch para traer estados actualizados post-proceso
    const movsActualizados = await getMovimientosBancarios(pais, { fechaDesde: "2026-01-01" });
    const ordenados = [...movsActualizados].sort((a, b) => b.fecha.localeCompare(a.fecha) || b.id - a.id);

    setMovBancarios(ordenados);
    setCuentas(ctas);
    setSaldosIniciales(saldosInicialesData?.data || []);

    // Resolver info (tipo_dte, folio, razon_social_receptor) de facturas emitidas
    // vinculadas vía multi: la tabla puente movimiento_bancario_facturas no tiene FK
    // formal a facturas_sii_emitidas, así que PostgREST no embebe el anidado. Fetch
    // separado y mapa en cliente.
    const idsEmitidasMulti = new Set();
    for (const m of ordenados) {
      for (const v of m.facturas_vinculadas || []) {
        if (v.factura_tipo === "sii_emitida" && v.factura_id != null) {
          idsEmitidasMulti.add(v.factura_id);
        }
      }
    }
    if (idsEmitidasMulti.size > 0) {
      const { data: facsInfo } = await client
        .from("facturas_sii_emitidas")
        .select("id, tipo_dte, folio, razon_social_receptor")
        .in("id", [...idsEmitidasMulti]);
      setFacturaInfoMap(
        new Map(
          (facsInfo || []).map((f) => [
            f.id,
            { tipo_dte: f.tipo_dte, folio: f.folio, razon_social_receptor: f.razon_social_receptor },
          ])
        )
      );
    } else {
      setFacturaInfoMap(new Map());
    }

    // Espejo para RECIBIDAS: mismo problema (MBF no tiene FK formal a facturas_sii_recibidas →
    // PostgREST no embebe el anidado en el caso múltiple). Fetch separado para que el badge C
    // muestre folio + razón social cuando la FK directa factura_sii_recibida_id quedó null.
    const idsRecibidasMulti = new Set();
    for (const m of ordenados) {
      for (const v of m.facturas_vinculadas || []) {
        if (v.factura_tipo === "sii_recibida" && v.factura_id != null) {
          idsRecibidasMulti.add(v.factura_id);
        }
      }
    }
    if (idsRecibidasMulti.size > 0) {
      const { data: recsInfo } = await client
        .from("facturas_sii_recibidas")
        .select("id, folio, razon_social_emisor, monto_total")
        .in("id", [...idsRecibidasMulti]);
      setFacturaRecibidaInfoMap(
        new Map(
          (recsInfo || []).map((f) => [
            f.id,
            { folio: f.folio, razon_social_emisor: f.razon_social_emisor, monto_total: f.monto_total },
          ])
        )
      );
    } else {
      setFacturaRecibidaInfoMap(new Map());
    }
    try {
      const pagosEmp = await getPagosPorMovimientos(ordenados.map((m) => m.id));
      setPagosEmpleadosRegistrados(pagosEmp);
    } catch {
      setPagosEmpleadosRegistrados([]);
    }

    // Vinculaciones forzadas por faltante (10.108 invertida): marca "forzado/pendiente" en el
    // histórico. Patrón silencioso: si la tabla no existe o falla, degrada a Map vacío (no rompe el
    // histórico). Filtrado por país (regla 8: la fila guarda el país del movimiento). Arranca vacía.
    const forzadosRows = await client
      .from("vinculaciones_forzadas")
      .select("movimiento_bancario_id, estado, faltante")
      .eq("pais", pais)
      .then((r) => r.data || [])
      .catch(() => []);
    setForzadosMap(
      new Map(forzadosRows.map((f) => [f.movimiento_bancario_id, { estado: f.estado, faltante: Number(f.faltante) }]))
    );

    setUltimaConsulta(new Date());
  }, [pais, ratesLoaded]);

  const cargarDatosFuturos = useCallback(async () => {
    if (!pais || pais === "Region" || !ratesLoaded) return;
    const [
      emitidas,
      pipelineData,
      recibidas,
      edificiosData,
      cuotasPagos,
      cuotasCobros,
      yaVinculadas,
      edificiosSaldoData,
      planPagosData,
      morasData,
      operacionesFactoringActivas,
      externasRes,
      proveedoresRes,
      movimientosCajaRes,
      saldosInicialesEntidades,
      cajaChicaGastosRes,
      mbfRes,
      comisionesFactoringRes,
      comisionesIngresoRes,
      cuotasCreditosData,
      moraNeteadaRes,
      f29Res,
      honorariosRes,
      rcvRes,
      overrideRes,
      cuotasActivos,
      activosConPlanData,
    ] = await Promise.all([
      getFacturasEmitidas(pais, { estado: "vinculada" }),
      getPipelineCobro(pais, monedaLocal),
      getFacturasRecibidas(pais, { estado_not: "ignorada" }),
      client.from("edificios").select("id, edificio, proveedor_id").eq("pais", pais),
      getCuotasPagosFuturas(pais),
      getCuotasCobrosFuturas(pais),
      getFacturasYaVinculadas(pais),
      getEdificiosSaldoPendiente({ pais, fechaInicio: "2026-01-01", monedaLocal }),
      getPlanPagosEdificios(pais).catch(() => []),
      getMorasPendientes(pais).catch(() => []),
      // Sin filtro de estado — necesitamos también liquidadas para que mapaFactoring marque
      // con badge F los movs históricos vinculados a ops ya cerradas.
      getOperacionesFactoring(pais).catch(() => []),
      // Fuente extra de "Por pagar". El dedup contra movs bancarios pagados lo hace
      // yaVinculadas.externas abajo — acá traemos sin pagada.
      client
        .from("facturas_externas")
        .select(
          "id, proveedor_id, proveedor_nombre, concepto, fecha_emision, periodo_mes, monto_total, moneda, numero_documento, estado, plazopago"
        )
        .eq("pais", pais)
        .neq("estado", "anulada"),
      client.from("proveedores").select("id, nombre, moneda_default").eq("pais", pais),
      // Movs caja y saldos iniciales: input del ledger para calcular saldo neto por proveedor.
      // Filtro estado != "anulado" + fecha desde FECHA_CORTE replican lo que hace EstadoCuenta.
      client
        .from("movimientos_caja")
        .select(
          "id, fecha, monto, moneda, tipo, entidad_tipo, entidad_id, descripcion, numero_documento, categoria_pago, estado, movimiento_bancario_id"
        )
        .eq("pais", pais)
        .neq("estado", "anulado")
        .gte("fecha", FECHA_CORTE),
      getSaldosIniciales(pais).catch(() => []),
      // Facturas SII recibidas ya vinculadas a caja chica → se excluyen de "Por pagar".
      client.from("caja_chica_gastos").select("factura_tipo, factura_id").eq("pais", pais),
      // Vinculaciones directas pago↔factura (MBF). El ledger las usa para saldar al 100% las
      // facturas vinculadas y excluir sus pagos del FIFO (evita residuos fantasma por TC).
      client.from("movimiento_bancario_facturas").select("movimiento_bancario_id, factura_tipo, factura_id, monto"),
      // Facturas SII recibidas de comisión de factoring (vinculadas a una operación): se
      // descuentan del giro, no son deuda real → se excluyen de "Por pagar" y del ledger.
      client.from("operaciones_factoring_comisiones").select("factura_sii_recibida_id"),
      // Comisiones neteadas de un ingreso (transferencia internacional recibida): mismo tratamiento.
      client.from("comisiones_ingreso_neteadas").select("factura_sii_recibida_id"),
      // Cuotas pendientes de créditos activos → proyección en el tab "Créditos".
      getCuotasPendientesProyeccion(pais).catch(() => []),
      // Facturas SII recibidas de mora saldada por neteo → se excluyen de "Por pagar" (gate propio).
      getFacturasMoraNeteada(pais).catch(() => []),
      // Fase B2 — fuentes reales para afinar el bloque F29 del tab Impuestos (solo Chile las
      // pueblan; en Perú vienen vacías → fallback a estimación en calcularImpuestos).
      client.from("f29_declaraciones").select("*").eq("pais", pais),
      client.from("honorarios_retencion").select("*").eq("pais", pais),
      client.from("rcv_periodo").select("*").eq("pais", pais),
      client.from("impuestos_f29_override").select("*").eq("pais", pais),
      // Plan de pagos de activos CapEx: cuotas pendientes (proyección por cuota) + activos con plan.
      getCuotasActivosFuturas(pais).catch(() => []),
      getActivosConPlan(pais).catch(() => new Set()),
    ]);
    setPipeline(pipelineData);
    setProveedoresPendientes(recibidas);
    const ccGastos = cajaChicaGastosRes?.data || [];
    const ccSiiIds = ccGastos.filter((g) => g.factura_tipo === "sii_recibida").map((g) => g.factura_id);
    setCajaChicaFacturaIds(new Set(ccSiiIds));
    // Set de facturas de comisión de factoring (local para el ledger de abajo + state para el filtro
    // de render en proyeccionesFuturas / countPorPagarPendientes).
    // Unión de facturas neteadas (comisión de factoring + comisión de ingreso/transferencia
    // recibida): ambas se excluyen de "Por pagar" y del ledger con el mismo criterio.
    const comisionIngresoIds = (comisionesIngresoRes?.data || []).map((r) => r.factura_sii_recibida_id);
    const comisionFactoringIds = new Set([
      ...(comisionesFactoringRes?.data || []).map((r) => r.factura_sii_recibida_id),
      ...comisionIngresoIds,
    ]);
    setFacturasComisionFactoringSet(comisionFactoringIds);
    // Set de facturas de mora saldada por neteo (estado='saldada_neteo'). Se excluyen SOLO de
    // "Por pagar" vía el predicado (no del ledger/FIFO — el saldo del proveedor no se toca acá).
    const moraNeteadaIds = new Set((moraNeteadaRes || []).map((r) => r.factura_sii_recibida_id));
    setFacturasMoraNeteadaSet(moraNeteadaIds);
    // Recurrentes se calcula después del Promise.all para que pueda deduplicar contra ambas
    // fuentes: SII recibidas (proveedores chilenos) y facturas externas (extranjeros como
    // OnSign, que no tienen DTE). Sin esta dedup unificada los meses con factura externa
    // cargada duplican la proyección recurrente en "Por pagar".
    const recurrentes = await getProveedoresRecurrentesEntries(
      pais,
      monedaLocal,
      recibidas,
      externasRes.data || [],
      movimientosCajaRes.data || []
    );
    setProveedoresRecurrentes(recurrentes);
    setImpuestosEntries(
      calcularImpuestos(
        emitidas,
        recibidas,
        pais,
        movimientosCajaRes.data || [],
        f29Res.data || [],
        honorariosRes.data || [],
        rcvRes.data || [],
        overrideRes.data || []
      )
    );
    // PREVIRED del tab RRHH (R2): subconjunto de movimientos_caja para conciliar a nivel mes.
    setMovimientosCajaCotizaciones(
      (movimientosCajaRes.data || []).filter((m) => m.categoria_pago === "cotizaciones_previsionales")
    );
    setEdificiosMap(Object.fromEntries((edificiosData.data || []).map((e) => [e.id, e.edificio])));
    setCuotasPagosPendientes(cuotasPagos || []);
    setCuotasCobrosPendientes(cuotasCobros || []);
    setCuotasActivosPendientes(cuotasActivos || []);
    setActivosConPlan(activosConPlanData || new Set());
    setYaVinculadasExternas(yaVinculadas.externas || new Set());
    setYaVinculadasEmitidas(yaVinculadas.emitidas || new Set());
    setEdificiosSaldo(edificiosSaldoData || []);
    setPlanPagosEdificios(planPagosData || []);
    setMorasPendientes(morasData || []);
    setCuotasCreditos(cuotasCreditosData || []);
    setOperacionesFactoring(operacionesFactoringActivas || []);
    const idsFactoring = new Set();
    // Map<movId, Array<{op, tipoVinc}>>. Un mismo movimiento bancario puede ser adelanto/excedente
    // de N operaciones (caso típico: Chita agrupa giros de varias facturas en un único depósito).
    // Lee de dos fuentes: las FKs legacy (_adelanto_id / _remanente_id) y la nueva tabla puente
    // operaciones_factoring_movimientos. Se deduplica por (movId, opId) para no contar dos veces
    // cuando una op pueblan ambos.
    const nuevoMapaFactoring = new Map();
    const pushEntry = (key, entry) => {
      idsFactoring.add(key);
      const existing = nuevoMapaFactoring.get(key) || [];
      if (existing.some((e) => e.op.id === entry.op.id && e.tipoVinc === entry.tipoVinc)) return;
      nuevoMapaFactoring.set(key, [...existing, entry]);
    };
    for (const op of operacionesFactoringActivas || []) {
      if (op.movimiento_bancario_adelanto_id) pushEntry(op.movimiento_bancario_adelanto_id, { op, tipoVinc: "Giro" });
      if (op.movimiento_bancario_remanente_id) {
        pushEntry(op.movimiento_bancario_remanente_id, { op, tipoVinc: "Excedente" });
      }
      for (const mv of op.movimientos_vinculados || []) {
        if (!mv.movimiento?.id) continue;
        const tipoVinc = mv.tipo === "adelanto" ? "Giro" : "Excedente";
        pushEntry(mv.movimiento.id, { op, tipoVinc });
      }
    }
    setMovimientosBancarioFactoring(idsFactoring);
    setMapaFactoring(nuevoMapaFactoring);

    // ─── Saldo neto pendiente por proveedor ───
    // Corre construirLedgerProveedor para cada proveedor con actividad en cualquier
    // fuente (SII recibidas + externas + movs caja). Convención del ledger:
    // saldo > 0 = el proveedor tiene saldo a su favor = le debemos → "Por pagar".
    // Epsilon 0.01 descarta saldos cuasi-cero (facturas + pagos calzando exacto).
    // USD se convierte a moneda local por fecha del item. UF rara en proveedores → identity.
    // Solo facturas desde FECHA_CORTE alimentan el ledger: las pre-corte (históricas 2025)
    // ya están incorporadas en el saldo inicial por entidad. Incluirlas las doble-contaría y
    // mis-atribuiría pagos 2026 vía FIFO a facturas viejas. Las nulas se conservan (defensivo).
    const recibidasArr = (recibidas || []).filter((f) => !f.fecha_emision || f.fecha_emision >= FECHA_CORTE);
    const externasArr = externasRes.data || [];
    const movsCajaArr = movimientosCajaRes.data || [];
    const saldosInicArr = saldosInicialesEntidades || [];
    const proveedoresArr = proveedoresRes.data || [];
    const vinculacionesMbfArr = mbfRes.data || [];
    // Raw arrays a state — el filtrado por ya-vinculadas / estado se hace en el useMemo
    // (mismo patrón que proveedoresPendientes + yaVinculadasExternas). La query ya
    // descarta estado='anulada'.
    setFacturasExternasPendientes(externasArr);
    // Proveedores-edificio: su id está en edificios.proveedor_id. Su deuda se mide solo por la
    // comisión de sus edificios (getEdificiosSaldoPendiente), NO como obligación de proveedor.
    // Se excluyen del universo del ledger de proveedor → no aparecen en "Por pagar" ni generan
    // residuo "Sin folio". Comparación por ID (regla 8). Mientras edificios.proveedor_id esté
    // vacío el Set es vacío y nada cambia.
    const provIdsEdificio = new Set((edificiosData.data || []).map((e) => e.proveedor_id).filter((id) => id != null));
    const provIdsConActividad = new Set();
    for (const f of recibidasArr)
      if (f.proveedor_id && !provIdsEdificio.has(f.proveedor_id)) provIdsConActividad.add(f.proveedor_id);
    for (const f of externasArr)
      if (f.proveedor_id && !provIdsEdificio.has(f.proveedor_id)) provIdsConActividad.add(f.proveedor_id);
    for (const m of movsCajaArr) {
      if (m.entidad_tipo === "proveedor" && m.entidad_id && !provIdsEdificio.has(m.entidad_id))
        provIdsConActividad.add(m.entidad_id);
    }
    const nombreProvMap = Object.fromEntries(proveedoresArr.map((p) => [p.id, getNombreProveedor(p)]));
    const tdcLocal = (monto, monedaOrigen, fecha) => {
      if (!monto) return 0;
      const origen = monedaOrigen || monedaLocal;
      if (origen === monedaLocal) return monto;
      if (origen === "USD") {
        const rate = getDolarValue(fecha, pais) || getDolarFallback(pais) || 1;
        return Math.round(monto * rate);
      }
      return monto;
    };
    // tdcUSD: simétrico — convierte local → USD dividiendo por dólar del día. Para
    // proveedores predominantemente USD genera una segunda entry en USD (sin convertir).
    const tdcUSD = (monto, monedaOrigen, fecha) => {
      if (!monto) return 0;
      const origen = monedaOrigen || monedaLocal;
      if (origen === "USD") return monto;
      if (origen === monedaLocal) {
        const rate = getDolarValue(fecha, pais) || getDolarFallback(pais) || 1;
        return rate > 0 ? Math.round(monto / rate) : 0;
      }
      return monto;
    };
    const saldosProvs = [];
    const nuevoSaldosMap = new Map();
    const nuevoProvMonedaMap = new Map();
    const residuosArr = [];
    for (const provId of provIdsConActividad) {
      const saldoInicial = saldosInicArr.find((s) => s.entidad_tipo === "proveedor" && s.entidad_id === provId) || null;
      const ledgerArgs = {
        provId,
        siiRecibidas: recibidasArr,
        facturasExternas: externasArr,
        movimientosCaja: movsCajaArr,
        saldoInicial,
        monedaPrincipal: monedaLocal,
        vinculacionesDirectas: vinculacionesMbfArr,
        excluirProvIds: provIdsEdificio,
        facturasComisionFactoring: comisionFactoringIds,
      };
      const nombre = nombreProvMap[provId] || `Proveedor #${provId}`;
      // Determinar moneda del proveedor desde moneda_default. Fallback: monedaLocal.
      const provObj = proveedoresArr.find((p) => p.id === provId);
      const provMoneda = provObj?.moneda_default || monedaLocal;
      const esProvUSD = provMoneda === "USD";
      const tdcProv = esProvUSD ? tdcUSD : tdcLocal;
      // Guardamos la moneda en que quedan expresados los saldos de este proveedor para que
      // el consumidor en proyeccionesFuturas re-exprese al display con la moneda correcta.
      nuevoProvMonedaMap.set(provId, provMoneda);

      const { entries, saldosPorFactura } = construirLedgerProveedor({ ...ledgerArgs, toDisplayCurrency: tdcProv });
      if (entries.length === 0) continue;
      const saldo = entries[0].saldo;
      if (saldo > 0.01) {
        saldosProvs.push({ entidadId: provId, nombre, saldo, moneda: provMoneda });
      }
      // Acumular saldosPorFactura del ledger (saldoDisplay en moneda del proveedor — USD
      // para Mailhos, monedaLocal para el resto). El consumidor en useMemo aplica aDisplay
      // tratándolo como factura.moneda — válido cuando provMoneda === f.moneda (caso común).
      // La key especial "RESIDUO" sale del Map general y va a residuosArr para emitirse
      // como fila "Saldo a cuenta" separada (Fuente 3 del useMemo).
      for (const [key, { saldoDisplay }] of saldosPorFactura) {
        if (key === "RESIDUO") {
          residuosArr.push({ provId, nombre, saldoDisplay, moneda: provMoneda });
        } else {
          nuevoSaldosMap.set(key, saldoDisplay);
        }
      }
    }
    setSaldosPendientesProveedores(saldosProvs);
    setSaldosPorFacturaMap(nuevoSaldosMap);
    setProvMonedaMap(nuevoProvMonedaMap);
    setResiduosProveedoresPendientes(residuosArr);

    // CapEx planificado — DOS fuentes desde el bloque Stock:
    //  1) activos sueltos planificados (modal Inversión por edificio, camino legacy)
    //  2) lotes_compra planificados (compra de stock) → 1 línea por lote, NO N por unidad
    // El guard `lote_id === null` en la rama de activos es red de seguridad: por el
    // invariante del bloque una unidad nunca nace `planificado`, así que hoy no filtra
    // nada (las 12 filas existentes tienen lote_id null), pero evita el doble conteo si
    // alguna vez se escribiera una unidad planificada por error.
    const [capexActivos, capexLotesData, componentesLotesData] = await Promise.all([
      getActivos(pais, { estado: "planificado" }),
      getLotes(pais, { estado: "planificado" }),
      // Componentes de costo pendientes (transporte/aduana/otro). SIN filtro de fecha → incluye
      // vencidos (10.155); se muestran en rojo en su fecha comprometida, igual que la línea FOB.
      getCostosLotesFuturos(pais).catch(() => []),
    ]);
    setCapexEntries((capexActivos || []).filter((a) => a.lote_id === null || a.lote_id === undefined));
    setCapexLotes(capexLotesData || []);
    setComponentesLotes(componentesLotesData || []);
    const plazosMap = await getPlazosCobroParaSII(emitidas);
    // Cobertura de cobro REAL para "Por cobrar" (fuente de verdad: banco + factoring,
    // NO el campo manual facturafechacobro). Se arma desde datos ya cargados:
    //  - coberturaCobroMap: Σ monto de movimiento_bancario_facturas (sii_emitida) por factura.
    //    Reutiliza mbfRes (ya fetcheado) en vez de una query extra.
    //  - factoringLiquidadaIds: emitidas cobradas vía factor (op estado='liquidada'), tomando
    //    tanto la FK escalar como la tabla puente 1:N. operacionesFactoringActivas trae todas
    //    las ops (sin filtro de estado), así que no requiere query adicional.
    const coberturaCobroMap = new Map();
    (mbfRes.data || [])
      .filter((row) => row.factura_tipo === "sii_emitida")
      .forEach((row) => {
        coberturaCobroMap.set(row.factura_id, (coberturaCobroMap.get(row.factura_id) || 0) + Number(row.monto || 0));
      });
    const factoringLiquidadaIds = new Set();
    (operacionesFactoringActivas || [])
      .filter((op) => op.estado === "liquidada")
      .forEach((op) => {
        if (op.factura_sii_emitida_id) factoringLiquidadaIds.add(op.factura_sii_emitida_id);
        (op.facturas_vinculadas || []).forEach((fv) => {
          if (fv.factura_sii_emitida_id) factoringLiquidadaIds.add(fv.factura_sii_emitida_id);
        });
      });
    // Default de fecha de cobro esperada: fecha_emision + 45 días cuando no hay plazo definido.
    // Garantiza que toda emitida tenga fecha (se pinta en rojo si ya venció).
    const plazoDefault = (fechaEmision) => {
      if (!fechaEmision) return null;
      const d = new Date(`${fechaEmision}T00:00:00`);
      d.setDate(d.getDate() + 45);
      return d.toISOString().split("T")[0];
    };
    const emitidasConPlazos = emitidas.map((f) => {
      const key = `${f.venta_tipo}-${f.venta_ref_id}-${f.venta_ref_index ?? ""}`;
      const plazos = plazosMap.get(key) || { plazocobro: null, facturafechacobro: null };
      return {
        ...f,
        // SII-first (mismo criterio que facturafechacobro abajo): el plazocobro de la factura SII
        // (lo que escribe PlanCuotasModal → "Ajustar fecha") manda sobre el de la venta (plazos,
        // denormalizado). Antes venta-first → el override de fecha se ignoraba en "Por cobrar".
        plazocobro: f.plazocobro ?? plazos.plazocobro ?? plazoDefault(f.fecha_emision),
        // Hubo override explícito de fecha (no es el default +45) → enciende la pill verde.
        _plazoOverride: (f.plazocobro ?? plazos.plazocobro) != null,
        // SII-first (consistente con VentasFacturas/useVFDataLoading): el facturafechacobro de la
        // factura SII (lo que escribe el cobro reconciliado) manda sobre el de la venta (plazos,
        // que sale de sales.facturafechacobro, denormalizado/manual). Antes estaba invertido.
        facturafechacobro: f.facturafechacobro ?? plazos.facturafechacobro,
        moneda_dolar_origen: plazos.moneda_dolar_origen ?? false,
        coberturaMBF: coberturaCobroMap.get(f.id) || 0,
        esFactoringLiquidada: factoringLiquidadaIds.has(f.id),
      };
    });
    setPendientesCobro(emitidasConPlazos);
  }, [pais, monedaLocal, ratesLoaded]);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      await cargarDatosHistoricos();
      await cargarDatosFuturos();
    } catch {
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [cargarDatosHistoricos, cargarDatosFuturos]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Actualización optimista al confirmar vinculación: el modal devuelve el objeto del
  // movimiento bancario actualizado (con campos cambiados + embed de movimiento_caja)
  // y acá reemplazamos solo esa fila en el state local. Sin recargar nada más.
  // Empleado pasa _empleado=true + _pagosEmpleado (filas insertadas): el badge E depende de
  // pagosEmpleadosRegistrados (state separado), así que inyectamos esas filas a ese state
  // sin reload. Fallback a cargarDatos solo si no vinieron las filas.
  // Flujos de factoring: el payload trae _factoringOp y _factoringTipo (vincular) o
  // _desvinculaFactoring (desvincular). Mantienen sincronizados mapaFactoring y el Set
  // movimientosBancarioFactoring para que el badge F aparezca/desaparezca sin reload.
  // PASO 1 del split de D.2 "Compra de activo": marcar un CapEx planificado como ya pagado.
  // Solo cambia el estado del activo a 'ejecutado_sin_factura' → sale de la proyección (el loader
  // pide estado='planificado'). NO toca banco, NO crea caja, NO setea fecha_compra (no deprecia).
  // El cierre + factura es la Fase 3. Revert: editar el activo en GestionActivos y volver a 'planificado'.
  const handleMarcarCapexPagado = useCallback(
    async (entry) => {
      if (!entry?.activo_id) return;
      try {
        await actualizarActivo(entry.activo_id, { estado: "ejecutado_sin_factura" });
        await cargarDatosFuturos();
        toast.success("Marcado como pagado · pendiente de factura");
        setCapexPagadoModal(null);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error marcando CapEx como pagado:", err);
        toast.error("Error al marcar como pagado");
      }
    },
    [cargarDatosFuturos]
  );

  // Guarda el override manual de PPM/IUT del contador (bloque F29). Upsert preservando la otra
  // columna (se puede overridear solo una). Comparación/escritura por ID (RUT + periodo).
  const handleGuardarOverrideF29 = useCallback(
    async (valor) => {
      if (!overrideF29Modal) return;
      const { campo, periodo } = overrideF29Modal;
      const rut = RUT_EMPRESA_POR_PAIS[pais];
      if (!rut) return;
      try {
        const { data: existente } = await client
          .from("impuestos_f29_override")
          .select("ppm, iut")
          .eq("pais", pais)
          .eq("company_rut", rut)
          .eq("periodo", periodo)
          .maybeSingle();
        const fila = {
          pais,
          company_rut: rut,
          periodo,
          ppm: existente?.ppm ?? null,
          iut: existente?.iut ?? null,
          [campo]: valor,
        };
        const { error } = await client
          .from("impuestos_f29_override")
          .upsert(fila, { onConflict: "pais,company_rut,periodo" });
        if (error) throw error;
        setOverrideF29Modal(null);
        toast.success(`${campo.toUpperCase()} ${periodo} cargado`);
        await cargarDatosFuturos();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error guardando override F29:", err);
        toast.error("Error al guardar el valor");
      }
    },
    [overrideF29Modal, pais, cargarDatosFuturos]
  );

  const handleVincularSuccess = useCallback(
    (movActualizado) => {
      if (!movActualizado?.id) {
        cargarDatos();
        return;
      }

      if (movActualizado._empleado) {
        if (movActualizado._pagosEmpleado?.length) {
          setPagosEmpleadosRegistrados((prev) => [...prev, ...movActualizado._pagosEmpleado]);
        } else {
          cargarDatos(); // fallback defensivo si no vinieron las filas insertadas
        }
        return;
      }

      // Desvincular pago a empleado: sacar las filas de ese movimiento del state →
      // pagosMovIds recomputa y el badge E desaparece al instante, sin reload.
      if (movActualizado._empleadoDesvinculado) {
        setPagosEmpleadosRegistrados((prev) =>
          prev.filter((p) => p.movimiento_bancario_id !== movActualizado._movimientoBancarioId)
        );
        return;
      }

      setMovBancarios((prev) =>
        prev.map((m) => {
          if (m.id !== movActualizado.id) return m;
          const merged = { ...m, ...movActualizado };
          delete merged._reload;
          delete merged._empleado;
          delete merged._factoringOp;
          delete merged._factoringTipo;
          delete merged._desvinculaFactoring;
          return merged;
        })
      );

      // Desvincular pago completo (incl. forzado) o REGULARIZAR/DESHACER la regularización del forzado
      // con NC (F4.2/F4.3): en todos el payload no recalcula solo ni el histórico (forzadosMap → ⚠) ni
      // el ledger de futuros. Refrescar AMBOS, igual que el vincular. cargarDatosHistoricos recarga
      // forzadosMap → el ⚠ se va (fila borrada al desvincular, o 'resuelto' al regularizar) o REAPARECE
      // (vuelta a 'pendiente' al deshacer). Ver diagnóstico del forzado.
      if (movActualizado._desvinculado || movActualizado._forzadoResuelto) {
        cargarDatosHistoricos();
        cargarDatosFuturos();
      }

      // Vincular factura SII (recibida o emitida): el panel "Por pagar"/"Por cobrar" se
      // alimenta del ledger (saldosPorFacturaMap), no de un Set optimista. Recalcular los
      // futuros para que el saldo refleje el nuevo MBF (soporta N movimientos → 1 factura:
      // al cubrir el monto_total la factura desaparece; si es parcial muestra el remanente).
      // MULTI: las FK escalares (factura_sii_*_id) van en NULL, así que se detecta por la
      // categoria de la caja (pago_proveedor / cobro_factura). Sin esta rama, un multi refrescaba
      // el histórico (badge/tooltip) pero dejaba "Por pagar" con el saldo viejo. Vale para el
      // forzado y para cualquier vinculación multi. Ver 10.66 / diagnóstico del forzado.
      const categoriaCajaVinc = movActualizado.movimiento_caja?.categoria_pago;
      if (
        movActualizado.factura_sii_recibida_id ||
        movActualizado.factura_sii_emitida_id ||
        categoriaCajaVinc === "pago_proveedor" ||
        categoriaCajaVinc === "cobro_factura"
      ) {
        cargarDatosFuturos();
      }

      // Cobro SII emitida (single con FK, o MULTI con FK null + caja 'cobro_factura'): el merge
      // optimista de arriba NO trae las filas MBF nuevas ni el tipo_dte/folio de las facturas, así
      // que `facturaInfoMap` queda sin esos ids → el badge V no detecta exportación y el tooltip cae
      // en "Cobro SII" genérico en vez de "Cobro Exportación · Folios …". Rebuildear el histórico
      // realinea movBancarios (con facturas_vinculadas reales) + facturaInfoMap, que es la MISMA
      // fuente que usa la etiqueta del badge (esCobroExportacion). Ver 7.18 y caveat commit 5a87a98.
      const esCobroSiiEmitida =
        !!movActualizado.factura_sii_emitida_id || movActualizado.movimiento_caja?.categoria_pago === "cobro_factura";
      // Espejo para COMPRA SII MÚLTIPLE (badge C): al confirmar N facturas la FK escalar
      // factura_sii_recibida_id queda null, así que el merge optimista NO puebla `facturaRecibidaInfoMap`
      // con los folios/razón/monto nuevos → el tooltip del badge C queda sin detalle. Rebuildear el
      // histórico lo realinea (mismo motivo que el cobro emitido de arriba). Solo el caso multi (FK null);
      // el single trae factura_recibida en el payload y no lo necesita.
      const esCompraSiiRecibidaMulti =
        !movActualizado.factura_sii_recibida_id && movActualizado.movimiento_caja?.categoria_pago === "pago_proveedor";
      if (esCobroSiiEmitida || esCompraSiiRecibidaMulti) {
        cargarDatosHistoricos();
      }

      if (movActualizado._factoringOp && movActualizado._factoringTipo) {
        setMapaFactoring((prev) => {
          const next = new Map(prev);
          const existing = next.get(movActualizado.id) || [];
          next.set(movActualizado.id, [
            ...existing,
            { op: movActualizado._factoringOp, tipoVinc: movActualizado._factoringTipo },
          ]);
          return next;
        });
        setMovimientosBancarioFactoring((prev) => {
          const next = new Set(prev);
          next.add(movActualizado.id);
          return next;
        });
      } else if (movActualizado._desvinculaFactoring) {
        setMapaFactoring((prev) => {
          const next = new Map(prev);
          next.delete(movActualizado.id);
          return next;
        });
        setMovimientosBancarioFactoring((prev) => {
          const next = new Set(prev);
          next.delete(movActualizado.id);
          return next;
        });
      }

      // Vincular factura externa (single o multi): agregar IDs al Set de dedup para
      // que la factura desaparezca al instante del panel "Por pagar" sin reload.
      if (movActualizado._externasVinculadas?.length) {
        setYaVinculadasExternas((prev) => {
          const next = new Set(prev);
          movActualizado._externasVinculadas.forEach((id) => next.add(id));
          return next;
        });
      }

      // Desvincular factura externa: inverso. Sacar IDs del Set para que reaparezca
      // en "Por pagar" sin reload.
      if (movActualizado._externasDesvinculadas?.length) {
        setYaVinculadasExternas((prev) => {
          const next = new Set(prev);
          movActualizado._externasDesvinculadas.forEach((id) => next.delete(id));
          return next;
        });
      }

      // Sin esto, el filtro #4 de proyeccionesFuturas (`saldosPorFacturaMap.get('EXT:id')
      // === undefined → continue`) descarta la factura aunque salga del Set: el map se
      // construye en cargarDatosFuturos y al momento del load la factura estaba pagada
      // (saldo 0 → no entró al map). Inyectamos su monto para que reaparezca al instante.
      if (movActualizado._externasDesvinculadasMontos?.length) {
        setSaldosPorFacturaMap((prev) => {
          const next = new Map(prev);
          movActualizado._externasDesvinculadasMontos.forEach(({ id, monto }) => {
            next.set(`EXT:${id}`, monto);
          });
          return next;
        });
      }
    },
    [cargarDatos, cargarDatosFuturos, cargarDatosHistoricos]
  );

  const handleSyncBanco = useCallback(async () => {
    setSincronizandoBanco(true);
    try {
      // desde = fecha del último movimiento ya registrado en movimientos_bancarios (estado
      // cargado en cargarDatosHistoricos para el país actual). Si no hay ninguno, FECHA_CORTE.
      const ultimaFecha =
        movBancarios.length > 0
          ? movBancarios.reduce((max, m) => (m.fecha && m.fecha > max ? m.fecha : max), movBancarios[0].fecha)
          : FECHA_CORTE;
      const desde = ultimaFecha;
      // `hasta` = hoy + N días. Diagnóstico (dryRun, commit 243d12b1): Clay aplica un date_to
      // IMPLÍCITO = hoy cuando NO se le pasa date_to, así que omitir `hasta` NO alcanza (corta en
      // hoy igual). La única forma comprobada de traer asientos con FECHA VALOR FUTURA (el banco
      // registra pagos fechados al próximo día hábil) es mandarle a Clay un date_to en el futuro.
      // Se construye en hora LOCAL (getFullYear/Month/Date, NO toISOString) para no reintroducir
      // el desfase UTC. desde queda igual; la capa (b) del dedup descarta los ya existentes.
      const DIAS_VENTANA_FUTURA = 7;
      const hoy = new Date();
      const hastaDate = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + DIAS_VENTANA_FUTURA);
      const pad = (n) => String(n).padStart(2, "0");
      const hasta = `${hastaDate.getFullYear()}-${pad(hastaDate.getMonth() + 1)}-${pad(hastaDate.getDate())}`;
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/clay-import?pais=${pais}&desde=${desde}&hasta=${hasta}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      const resultados = data.resultados || [];
      const totalNuevos = resultados.reduce((s, r) => s + (r.nuevos || 0), 0);
      const erroresReales = resultados.filter((r) => r.error && !r.error.includes("Sin movimientos"));

      if (totalNuevos > 0) {
        toast.success(
          `Banco: ${totalNuevos} movimiento${totalNuevos === 1 ? "" : "s"} nuevo${totalNuevos === 1 ? "" : "s"} · actualizado.`
        );
      } else {
        const [, mmU, ddU] = ultimaFecha.split("-");
        toast(`Sin movimientos nuevos desde el último registrado (${ddU}/${mmU}).`, { duration: 7000 });
      }
      if (erroresReales.length > 0) {
        toast.error(
          `${erroresReales.length} cuenta${erroresReales.length === 1 ? "" : "s"} con error de sincronización`
        );
      }
      await cargarDatos();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error sincronizando banco:", err);
      toast.error("Error al actualizar banco");
    } finally {
      setSincronizandoBanco(false);
    }
  }, [pais, cargarDatos, movBancarios]);

  const handleSyncSII = useCallback(async () => {
    setSincronizandoSII(true);
    const fallidos = [];
    // 1. DTEs (sii-sync) — mantiene su parsing de novedades. Su error NO aborta los demás.
    try {
      const hoy = new Date();
      const periodo = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`;
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/sii-sync?periodo=${periodo}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      const nuevasEm = data.emitidas?.nuevas || 0;
      const nuevasRe = data.recibidas?.nuevas || 0;
      const totalNuevas = nuevasEm + nuevasRe;
      if (totalNuevas > 0) {
        toast.success(
          `${enteFiscal} (${periodo}): ${nuevasEm} emitida${nuevasEm === 1 ? "" : "s"} + ${nuevasRe} recibida${nuevasRe === 1 ? "" : "s"} nuevas`
        );
      } else {
        toast(
          `${enteFiscal} sin novedades para ${periodo}. ${data.clay_total || 0} DTEs revisados, todos ya estaban.`,
          {
            duration: 6000,
          }
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error sincronizando SII:", err);
      fallidos.push(enteFiscal);
    }

    // 2. Fuentes del bloque F29 (solo Chile): F29, honorarios y RCV en paralelo. Cada uno maneja
    // su error de forma independiente (allSettled) → si uno falla, los demás siguen y se avisa cuál.
    if (pais === "Chile") {
      const extras = [
        ["f29-sync", "F29"],
        ["honorarios-sync", "Honorarios"],
        ["rcv-sync", "RCV"],
      ];
      const resultados = await Promise.allSettled(extras.map(([fn]) => invocarEdgeSync(fn)));
      resultados.forEach((r, idx) => {
        if (r.status === "rejected") {
          // eslint-disable-next-line no-console
          console.error(`Error en ${extras[idx][0]}:`, r.reason);
          fallidos.push(extras[idx][1]);
        }
      });
    }

    if (fallidos.length > 0) {
      toast.error(`Sync con error: ${fallidos.join(", ")}`);
    }

    try {
      await cargarDatos();
    } finally {
      setSincronizandoSII(false);
    }
  }, [cargarDatos, enteFiscal, pais]);

  // Moneda en la que se muestra todo. Independiente de qué cuentas se incluyen
  // (eso lo controla `consolidado`). `convertirAUSD` solo aplica cuando estás en
  // la pestaña local — en USD ya está en dólares.
  const monedaDisplay = monedaVista === "USD" || convertirAUSD ? "USD" : monedaLocal;

  // Convierte un monto entre USD ↔ local usando la cotización de la fecha provista.
  // Para fechas futuras, getDolarValue cae en fallback a la fecha anterior más cercana
  // (la última cotización conocida), que es exactamente lo que queremos para proyecciones.
  const convertir = useCallback(
    (monto, monedaOrigen, monedaDestino, fecha) => {
      const n = Number(monto || 0);
      if (!n || monedaOrigen === monedaDestino) return n;
      const tasa = getDolarValue(fecha, pais) || getDolarFallback(pais) || 1;
      if (monedaOrigen === "USD") return Math.round(n * tasa);
      return Math.round(n / tasa);
    },
    [pais]
  );

  // Saldo derivado de una ancla histórica conocida (saldos_iniciales_cuentas) más la
  // suma de todos los movimientos posteriores. No depende de cuentas_bancarias.saldo_actual,
  // que tiene desfase entre el endpoint de saldo y el de movimientos en Clay.
  const calcularSaldoConAncla = useCallback(
    (moneda) => {
      const ancla = saldosIniciales.find((s) => s.moneda === moneda);
      if (!ancla) return 0;
      const ajuste = movBancarios.reduce((sum, m) => {
        if (m.cuenta_bancaria_id !== ancla.cuenta_bancaria_id) return sum;
        if (!m.fecha || m.fecha <= ancla.fecha) return sum;
        const monto = Number(m.monto || 0);
        return sum + (m.tipo === "ingreso" ? monto : -monto);
      }, 0);
      return Number(ancla.monto) + ajuste;
    },
    [saldosIniciales, movBancarios]
  );

  const saldoActualLocal = useMemo(() => calcularSaldoConAncla(monedaLocal), [calcularSaldoConAncla, monedaLocal]);

  const saldoActualUSD = useMemo(() => calcularSaldoConAncla("USD"), [calcularSaldoConAncla]);

  // Saldo expresado en monedaDisplay. Si consolidado, suma ambas cuentas (la otra
  // se convierte con la cotización de hoy → fallback a la última conocida).
  const hoyStr = new Date().toISOString().split("T")[0];
  const saldoActual = useMemo(() => {
    const localEnDisplay = convertir(saldoActualLocal, monedaLocal, monedaDisplay, hoyStr);
    const usdEnDisplay = convertir(saldoActualUSD, "USD", monedaDisplay, hoyStr);
    if (consolidado) return localEnDisplay + usdEnDisplay;
    return monedaVista === "USD" ? usdEnDisplay : localEnDisplay;
  }, [saldoActualLocal, saldoActualUSD, monedaLocal, monedaDisplay, consolidado, monedaVista, convertir, hoyStr]);

  const rangosFecha = useMemo(() => {
    // Todas las cotas se serializan con toLocalYMD (fecha LOCAL), NO toISOString()/UTC. Así "Hoy"
    // matchea la fecha local real (ej. 24/07 a las 20:11 de Chile) y desde/hasta quedan consistentes.
    // No cambia QUÉ cubre cada rango (lunes de semana, 1° de mes, 1-ene de YTD) — solo la zona.
    const hoy = new Date();
    const hoyStr = toLocalYMD(hoy);
    const diaSemana = hoy.getDay() === 0 ? 7 : hoy.getDay();
    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() - diaSemana + 1);
    // YTD llega hasta el ÚLTIMO asiento real de la cartola, no hasta hoy: los bancarios con
    // FECHA VALOR FUTURA (el banco los registra fechados al próximo día hábil) ya están en
    // movBancarios y ya suman en saldoActual (calcularSaldoConAncla, sin tope superior). Sin
    // este max, el filtro 1807 los descartaba y la fila tope (hoy) heredaba saldoActual → saldo
    // inflado por el net de las futuras. Con el max se listan arriba y el walk-back del Modo B
    // cuadra peso a peso. Hoy/Semana/Mes siguen capando en hoy (usan Modo A / acumulador).
    const maxFechaMov =
      movBancarios.length > 0
        ? movBancarios.reduce((max, m) => (m.fecha && m.fecha > max ? m.fecha : max), movBancarios[0].fecha)
        : hoyStr;
    const ytdHasta = maxFechaMov > hoyStr ? maxFechaMov : hoyStr;
    return {
      hoy: { desde: hoyStr, hasta: hoyStr },
      esta_semana: { desde: toLocalYMD(lunes), hasta: hoyStr },
      este_mes: { desde: toLocalYMD(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), hasta: hoyStr },
      ytd: { desde: toLocalYMD(new Date(hoy.getFullYear(), 0, 1)), hasta: ytdHasta },
    };
  }, [movBancarios]);

  // Lista de meses para el dropdown: desde el mes más antiguo con movimientos cargados
  // (o enero 2026 como fallback) hasta el mes anterior al actual. El mes en curso tiene
  // su propio botón "Este mes". El límite es dinámico para que escale conforme se cargan
  // datos históricos más antiguos.
  const mesesDropdown = useMemo(() => {
    const opciones = [];
    const hoy = new Date();
    const fechaMasAntigua =
      movBancarios.length > 0
        ? movBancarios.reduce((min, m) => (m.fecha && m.fecha < min ? m.fecha : min), movBancarios[0].fecha)
        : "2026-01-01";
    const [yLim, mLim] = fechaMasAntigua.split("-").map(Number);
    const limite = new Date(yLim, mLim - 1, 1);

    let cursor = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    while (cursor >= limite) {
      const y = cursor.getFullYear();
      const m = cursor.getMonth();
      const mm = String(m + 1).padStart(2, "0");
      const labelMes = cursor.toLocaleDateString("es", { month: "long" }).replace(/^\w/, (c) => c.toUpperCase());
      opciones.push({
        key: `mes_${y}-${mm}`,
        label: `${labelMes} ${y}`,
        desde: `${y}-${mm}-01`,
        hasta: new Date(y, m + 1, 0).toISOString().split("T")[0],
      });
      cursor = new Date(y, m - 1, 1);
    }
    return opciones;
  }, [movBancarios]);

  // Base del histórico: filtros de la barra (cuenta/moneda + tipo + fecha + Pendiente + búsqueda),
  // SIN el toggle de atención (⚠) y SIN el cálculo de saldo. Se comparte entre movimientosFiltrados
  // y el enabled/disabled del botón de atención (que mira si hay forzados en ESTA vista base).
  const movimientosBaseHistorico = useMemo(() => {
    const cuentaIdsValidos = consolidado
      ? new Set(cuentas.map((c) => c.id))
      : new Set(cuentas.filter((c) => c.moneda === monedaVista).map((c) => c.id));
    let lista = movBancarios.filter((m) => cuentaIdsValidos.has(m.cuenta_bancaria_id));
    if (filtroHistorico === "ingresos") lista = lista.filter((m) => m.tipo === "ingreso");
    if (filtroHistorico === "egresos") lista = lista.filter((m) => m.tipo === "egreso");
    const rango =
      filtroTiempo === "hoy"
        ? rangosFecha.hoy
        : filtroTiempo === "esta_semana"
          ? rangosFecha.esta_semana
          : filtroTiempo === "este_mes"
            ? rangosFecha.este_mes
            : filtroTiempo === "ytd"
              ? rangosFecha.ytd
              : mesesDropdown.find((m) => m.key === filtroTiempo);
    if (rango) lista = lista.filter((m) => m.fecha >= rango.desde && m.fecha <= rango.hasta);
    // Mismo predicado y mismos sets que el tinte naranja (fila) y el badge → fuente única.
    if (soloPendientes) lista = lista.filter((m) => isMovPendiente(m, pagosMovIds, movimientosBancarioFactoring));
    if (busquedaHistorico.trim()) {
      const q = busquedaHistorico.trim().toLowerCase();
      lista = lista.filter(
        (m) =>
          (m.descripcion_raw || "").toLowerCase().includes(q) ||
          String(Math.abs(Math.round(Number(m.monto || 0)))).includes(q)
      );
    }
    return lista;
  }, [
    movBancarios,
    cuentas,
    monedaVista,
    consolidado,
    filtroHistorico,
    filtroTiempo,
    rangosFecha,
    mesesDropdown,
    soloPendientes,
    pagosMovIds,
    movimientosBancarioFactoring,
    busquedaHistorico,
  ]);

  // ¿Hay algún movimiento con forzado pendiente en la vista base (antes del toggle de atención)?
  // Gobierna el enabled/disabled del botón ⚠: solo tiene sentido filtrar si hay algo que mostrar.
  const hayAtencionEnVista = useMemo(
    () => movimientosBaseHistorico.some((m) => forzadosMap.get(m.id)?.estado === "pendiente"),
    [movimientosBaseHistorico, forzadosMap]
  );

  // Auto-clear: si el toggle de atención está activo y la vista base deja de tener forzados
  // pendientes (cambió fecha/tipo/búsqueda), apagarlo para no quedar con la lista vacía.
  useEffect(() => {
    if (soloAtencion && !hayAtencionEnVista) setSoloAtencion(false);
  }, [soloAtencion, hayAtencionEnVista]);

  const movimientosFiltrados = useMemo(() => {
    // Mapa cuentaId → moneda de la cuenta (para el enriquecido).
    const cuentaMoneda = Object.fromEntries(cuentas.map((c) => [c.id, c.moneda]));
    // Toggle de atención (⚠): filtro AND independiente que se apila sobre la vista base.
    const lista = soloAtencion
      ? movimientosBaseHistorico.filter((m) => forzadosMap.get(m.id)?.estado === "pendiente")
      : movimientosBaseHistorico;
    // Enriquecemos primero (monto convertido + moneda origen). El cálculo del saldo
    // depende de si hay algún filtro activo.
    const enriched = lista.map((m) => {
      const monedaOrigen = cuentaMoneda[m.cuenta_bancaria_id] || monedaLocal;
      const montoEnDisplay = convertir(Number(m.monto || 0), monedaOrigen, monedaDisplay, m.fecha);
      return { ...m, monto_display: montoEnDisplay, moneda_origen: monedaOrigen };
    });

    const hayFiltroActivo =
      busquedaHistorico.trim() !== "" ||
      filtroHistorico !== "todo" ||
      filtroTiempo !== "ytd" ||
      soloPendientes ||
      soloAtencion;

    if (hayFiltroActivo) {
      // Con filtro activo el saldo "real" de cada fila sería inconsistente: la lista
      // oculta movs intermedios, así que caminar hacia atrás desde saldoActual da
      // valores que no coinciden con cartola. En su lugar mostramos el acumulado
      // running de la selección, partiendo de 0 en el más antiguo (sirve como
      // "¿cuánto suman los movs visibles hasta acá?"). La lista entra en orden DESC,
      // calculamos en ASC y devolvemos en DESC para no alterar el render.
      const asc = [...enriched].reverse();
      let acc = 0;
      const ascConSaldo = asc.map((m) => {
        acc += m.tipo === "ingreso" ? m.monto_display : -m.monto_display;
        return { ...m, saldo_calculado: acc };
      });
      return ascConSaldo.reverse();
    }

    // Sin filtro activo: saldo "real" — partir del saldo de ahora y deshacer hacia atrás.
    let saldo = saldoActual;
    return enriched.map((m) => {
      const entry = { ...m, saldo_calculado: saldo };
      if (m.tipo === "ingreso") saldo -= m.monto_display;
      else saldo += m.monto_display;
      return entry;
    });
  }, [
    movimientosBaseHistorico,
    cuentas,
    monedaLocal,
    monedaDisplay,
    saldoActual,
    convertir,
    soloAtencion,
    forzadosMap,
    busquedaHistorico,
    filtroHistorico,
    filtroTiempo,
    soloPendientes,
  ]);

  // Predicado único de inclusión a nivel FACTURA para "Por pagar" (Fuente 1, SII recibidas).
  // Fuente de verdad compartida entre el render de filas (proyeccionesFuturas) y el badge
  // (countPorPagarPendientes): mientras ambos lo usen, no pueden divergir ante un nuevo gate
  // (regla 10.1). NO gatea por estado_vinculacion — una factura "vinculada" pagada solo
  // parcialmente conserva saldo FIFO y debe contar/mostrarse; la exclusión de pagadas la hace
  // el FIFO. Excluye: históricas pre-corte, NC (tipo_dte=61, regla 10.42), caja chica (10.39),
  // comisiones factoring/recepción neteadas, mora saldada por neteo, y facturas saldadas al 100%
  // por el ledger FIFO.
  // Predicado extraído a util compartido (Fase 4, regla 10.1): mismo criterio que consume el
  // contador de EstadoCuenta. El useCallback envuelve la llamada para preservar deps/memoización.
  const facturaSiiPorPagar = useCallback(
    (f) =>
      facturaSiiPorPagarUtil(f, {
        cajaChicaFacturaIds,
        facturasComisionFactoringSet,
        facturasMoraNeteadaSet,
        saldosPorFacturaMap,
        FECHA_CORTE,
      }),
    [cajaChicaFacturaIds, facturasComisionFactoringSet, facturasMoraNeteadaSet, saldosPorFacturaMap]
  );

  const proyeccionesFuturas = useMemo(() => {
    const hoy = new Date().toISOString().split("T")[0];
    // Techo por defecto cuando NO hay filtro temporal activo: 31/12 del año siguiente.
    // Acota las fuentes que proyectan sin límite (créditos, cartelería) a la ventana visible.
    const FECHA_TECHO_DEFAULT = `${new Date().getFullYear() + 1}-12-31`;
    // Filtro temporal: si está activo, calcular la fecha de corte como string YYYY-MM-DD
    // para comparar lex contra entry.fecha_display (también YYYY-MM-DD).
    const fechaCorte = filtroTemporalKey
      ? calcularFiltrosTemporales().find((f) => f.key === filtroTemporalKey)?.fechaCorte
      : null;
    const fechaCorteStr = fechaCorte
      ? `${fechaCorte.getFullYear()}-${String(fechaCorte.getMonth() + 1).padStart(2, "0")}-${String(fechaCorte.getDate()).padStart(2, "0")}`
      : null;
    // Con corte temporal activo: filtro estricto, las entries sin fecha (fecha_display null)
    // quedan fuera — no podemos ubicarlas antes ni después del corte con certeza.
    // Sin corte activo: techo por defecto (31/12 año siguiente) y las entries sin fecha
    // SÍ pasan (son obligaciones sin fecha definida que deben verse en la vista completa).
    const dentroDeCorte = (e) =>
      fechaCorteStr
        ? !!e.fecha_display && e.fecha_display <= fechaCorteStr
        : !e.fecha_display || e.fecha_display <= FECHA_TECHO_DEFAULT;
    const filtrarPorCorte = (arr) => arr.filter(dentroDeCorte);
    const mostrarTodo = filtrosActivos.includes("todo");
    const mostrarFacturado = mostrarTodo || filtrosActivos.includes("cobros");
    const mostrarBacklog = mostrarTodo || filtrosActivos.includes("backlog");
    const mostrarProveedoresRecurrentes = mostrarTodo || filtrosActivos.includes("proveedores_recurrentes");
    const mostrarProveedoresFact = mostrarTodo || filtrosActivos.includes("proveedores_facturados");
    const mostrarProveedores = mostrarProveedoresRecurrentes || mostrarProveedoresFact;
    const mostrarRRHH = mostrarTodo || filtrosActivos.includes("rrhh");
    const mostrarImpuestos = mostrarTodo || filtrosActivos.includes("impuestos");
    const esUSD = monedaVista === "USD";
    // Cuándo incluir items USD vs local. Si consolidado, ambos. Si no, solo los de la moneda activa.
    const incluirUSD = consolidado || esUSD;
    const incluirLocal = consolidado || !esUSD;

    // Convierte un importe en moneda origen a la moneda de display, usando la fecha del item.
    const aDisplay = (monto, monedaOrig, fecha) => convertir(monto, monedaOrig, monedaDisplay, fecha);

    const mapFacturado = (f) => {
      // monto_total siempre está en moneda local (SII reporta en CLP/PEN, también
      // para facturas con origen USD). moneda_origen es la moneda "natural" de la
      // operación desde la perspectiva del usuario y se usa para badge/filtro.
      const monedaOrig = f.moneda_dolar_origen ? "USD" : monedaLocal;
      // Parcial: si hay cobro bancario (MBF) que no cubre el total, proyectar solo el saldo.
      const cobrado = f.coberturaMBF || 0;
      let saldoPorCobrar = Math.max(Number(f.monto_total || 0) - cobrado, 0);
      // Tolerancia de redondeo simétrica al ledger de pagos (misma constante): si TRAS cobrar
      // (cobrado>0) queda una miga ≤ umbral, la factura se considera cobrada al 100% → saldo 0
      // (la fila no se proyecta, ver guard del push). Guarda IMPRESCINDIBLE: solo si hubo cobro;
      // una emitida sin cobro conserva su saldo entero. monto_total real intacto. saldoPorCobrar
      // está en moneda local (monto_total siempre CLP/PEN, también para facturas USD) → el umbral
      // local aplica directo. Hoy no-op (0 residuos de cobro); simétrico por consistencia.
      if (cobrado > 0 && saldoPorCobrar > 0.01 && saldoPorCobrar <= TOLERANCIA_REDONDEO_LOCAL) {
        saldoPorCobrar = 0;
      }
      const esParcial = cobrado > 0 && saldoPorCobrar > 0 && cobrado < Number(f.monto_total || 0);
      const importeEnMoneda = aDisplay(saldoPorCobrar, monedaLocal, f.fecha_emision);
      return {
        ...f,
        fecha_display: f.plazocobro || null,
        importeEnMoneda,
        moneda_origen: monedaOrig,
        tipo: "facturado",
        folio: f.folio,
        _parcial: esParcial,
        // Pill verde: la fecha de cobro fue overrideada a mano (no es el default +45 días).
        tiene_override: f._plazoOverride,
        vencida: !!f.plazocobro && f.plazocobro < hoy,
      };
    };
    const mapPipeline = (p) => {
      // pipeline trae importe (USD si moneda_dolar=true) y importeCLP (local).
      const monedaOrig = p.moneda_dolar ? "USD" : monedaLocal;
      const importeOrig = p.moneda_dolar ? p.importe : (p.importeCLP ?? p.importe);
      return {
        ...p,
        fecha_display: p.fecha_estimada,
        importeEnMoneda: aDisplay(Number(importeOrig || 0), monedaOrig, p.fecha_estimada),
        moneda_origen: monedaOrig,
        razon_social_receptor: p.cliente,
        tipo: "pipeline",
        // Vencida: fecha proyectada ya pasada (mismo criterio que mapFactura, regla 10.68).
        // El Backlog ya no descarta vencidas; se muestran con la fecha en rojo en el render.
        vencida: !!p.fecha_estimada && p.fecha_estimada < hoy,
      };
    };
    // Fuente de verdad de "cobrada": respaldo bancario (MBF) o factoring liquidada — NO el
    // campo manual facturafechacobro (se setea desde múltiples vías sin respaldo, p.ej.
    // marcarFacturaCobrada de FlujoCaja V1). Las vencidas no cobradas SÍ son por cobrar.
    const facturadoFiltrado = pendientesCobro
      .filter((f) => {
        if (f.esFactoringLiquidada) return false; // cobrada vía factor
        // Cobrada por cualquier vía bancaria: FK directa + cuotas completas + MBF (Σ ≥ total).
        // yaVinculadasEmitidas NO incluye parciales MBF → esas siguen acá con su saldo.
        if (yaVinculadasEmitidas.has(f.id)) return false;
        return true; // pendiente total o parcial (incluye vencidas)
      })
      .filter((f) => f.estado_factoring !== "cedida")
      .filter((f) => (f.moneda_dolar_origen === true ? incluirUSD : incluirLocal));
    // Para cada factura emitida, verificar si tiene cuotas de cobro
    const facturadoEntries = [];
    if (mostrarFacturado) {
      for (const f of facturadoFiltrado) {
        // monto_cuota está en la misma moneda que monto_total (CLP/PEN). monedaOrig
        // refleja la moneda "natural" de la operación para badge/filtro.
        const monedaOrig = f.moneda_dolar_origen ? "USD" : monedaLocal;
        const cuotasFactura = cuotasCobrosPendientes.filter((c) => c.factura_sii_emitida_id === f.id);
        if (cuotasFactura.length > 0) {
          cuotasFactura.forEach((c) => {
            facturadoEntries.push({
              ...f,
              fecha_display: c.fecha_vencimiento,
              importeEnMoneda: aDisplay(Number(c.monto_cuota), monedaLocal, c.fecha_vencimiento),
              moneda_origen: monedaOrig,
              razon_social_receptor: f.razon_social_receptor,
              tipo: "facturado",
              folio: f.folio,
              cuota_actual: c.numero_cuota,
              // M = total del plan (todas las cuotas), NO solo las pendientes — viene enriquecido
              // desde getCuotasCobrosFuturas. Fallback a cuotasFactura.length por robustez.
              total_cuotas: c.total_cuotas > 0 ? c.total_cuotas : cuotasFactura.length,
              // Cada fecha_vencimiento la fijó el usuario al armar el plan → toda cuota es un
              // override → pill verde (mismo criterio que las cuotas de pago, commit 9f61899).
              tiene_override: true,
              // Cuota comprometida ya vencida → texto de fecha en rojo (reusa el flag `vencida`
              // del render, mismo criterio string que la rama sin-cuotas en línea ~1697).
              vencida: c.fecha_vencimiento < hoy,
            });
          });
        } else {
          const entry = mapFacturado(f);
          // La tolerancia de redondeo puede dejar importe 0 (miga cobrada) → no proyectar la fila.
          if (entry.importeEnMoneda > 0) facturadoEntries.push(entry);
        }
      }
    }

    // Facturas cedidas al factoring — dos entries por operación: giro a recibir
    // (anticipo neto de costos en fecha_anticipo) y excedente (en fecha_cobro_final).
    // Cada entry desaparece cuando el movimiento bancario correspondiente queda vinculado
    // (movimiento_bancario_adelanto_id / movimiento_bancario_remanente_id no nulos).
    const factoringEntries = [];
    if (mostrarFacturado) {
      // Las proyecciones futuras solo se generan para ops activas. Liquidadas / anuladas
      // viven en el state para que el panel histórico pueda marcar sus movs con badge F.
      for (const op of operacionesFactoring.filter((o) => o.estado === "activa")) {
        const proveedorFactoring = op.proveedor?.nombre || "Factoring";
        const cliente = op.factura?.razon_social_receptor || "";
        const tieneAdelantoVinc =
          !!op.movimiento_bancario_adelanto_id ||
          (op.movimientos_vinculados || []).some((mv) => mv.tipo === "adelanto");
        const tieneRemanenteVinc =
          !!op.movimiento_bancario_remanente_id ||
          (op.movimientos_vinculados || []).some((mv) => mv.tipo === "remanente");
        if (op.giro_a_recibir > 0 && !tieneAdelantoVinc) {
          const fechaGiro = op.fecha_anticipo || op.fecha_operacion;
          factoringEntries.push({
            id: `factoring-giro-${op.id}`,
            factura_sii_emitida_id: op.factura_sii_emitida_id,
            fecha_display: fechaGiro,
            importeEnMoneda: aDisplay(Number(op.giro_a_recibir), monedaLocal, fechaGiro),
            moneda_origen: monedaLocal,
            moneda_dolar_origen: false,
            razon_social_receptor: `Fact. ${proveedorFactoring} — ${cliente}`,
            tipo: "facturado",
            folio: op.factura?.folio || null,
            es_factoring: true,
          });
        }
        const remanente = op.monto_remanente ?? Number(op.monto_factura || 0) - Number(op.capital_adelantado || 0);
        if (remanente > 0 && !tieneRemanenteVinc) {
          factoringEntries.push({
            id: `factoring-excedente-${op.id}`,
            factura_sii_emitida_id: op.factura_sii_emitida_id,
            fecha_display: op.fecha_cobro_final,
            importeEnMoneda: aDisplay(remanente, monedaLocal, op.fecha_cobro_final),
            moneda_origen: monedaLocal,
            moneda_dolar_origen: false,
            razon_social_receptor: `Fact. ${proveedorFactoring} — Excedente — ${cliente}`,
            tipo: "facturado",
            folio: op.factura?.folio || null,
            es_factoring: true,
          });
        }
      }
    }

    const pipe = mostrarBacklog
      ? pipeline.map(mapPipeline).filter((p) => (p.moneda_dolar ? incluirUSD : incluirLocal) && p.importeEnMoneda > 0)
      : [];

    // "Por pagar" — itera las 2 fuentes individuales (SII recibidas + externas)
    // y genera una fila por folio (sin plan de cuotas) o N filas por folio (con cuotas).
    // El saldo consolidado por proveedor sigue calculándose en cargarDatosFuturos
    // (saldosPendientesProveedores) pero no se usa acá: el agregado vive en ResumenSaldo.
    // Las filas son el detalle operativo. Comparación por ID (regla 10.7).
    const provsEntries = [];

    // Fuente 1 — SII recibidas: descarta ya vinculadas a movs bancarios y las que el
    // usuario marcó como "vinculada" sin movimiento (estado_vinculacion). SII siempre en
    // moneda local (CLP/PEN) — no hay branch USD. Excluye notas de crédito (tipo_dte=61):
    // reducen deuda, no son pagos pendientes — su efecto neto ya está reflejado en el
    // ledger consolidado (ResumenSaldo).
    if (incluirLocal) {
      for (const f of proveedoresPendientes) {
        // Gate de inclusión a nivel factura — fuente única compartida con el badge
        // countPorPagarPendientes (regla 10.1). Encapsula corte de fecha, NC (10.42),
        // caja chica (10.39), comisiones factoring/recepción neteadas y saldada-FIFO.
        if (!facturaSiiPorPagar(f)) continue;

        // Saldo FIFO restante — se consulta ANTES de la bifurcación cuotas/sin-cuotas para que
        // AMBAS ramas respeten lo ya pagado. El predicado ya garantizó que existe (con
        // proveedor_id) o que no hay FIFO (sin proveedor_id → fallback a monto_total).
        const saldoFIFO = f.proveedor_id ? saldosPorFacturaMap.get(`SII:${f.id}`) : Number(f.monto_total || 0);

        const cuotasFactura = cuotasPagosPendientes.filter((c) => c.factura_sii_recibida_id === f.id);
        if (cuotasFactura.length > 0) {
          // Mostrar las cuotas pero CAPADAS al saldo FIFO disponible: una factura con plan de
          // pagos pero ya pagada (parcial/total) por caja no debe sobre-mostrarse (caso Blackim
          // 958/989: el plan tenía el bruto, los pagos reales vivían en movimientos_caja).
          let saldoDisponible = saldoFIFO;
          for (const c of cuotasFactura) {
            if (saldoDisponible <= 0.01) break;
            const montoCuota = Math.min(Number(c.monto_cuota), saldoDisponible);
            saldoDisponible -= montoCuota;
            provsEntries.push({
              ...f,
              fecha_display: c.fecha_vencimiento,
              importeEnMoneda: -aDisplay(montoCuota, monedaLocal, c.fecha_vencimiento),
              moneda_origen: monedaLocal,
              razon_social_receptor: f.razon_social_emisor,
              tipo: "proveedor",
              folio: f.folio,
              cuota_actual: c.numero_cuota,
              // M = total del plan (todas las cuotas), NO solo las pendientes — viene enriquecido
              // desde getCuotasPagosFuturas. Fallback a cuotasFactura.length por robustez.
              total_cuotas: c.total_cuotas > 0 ? c.total_cuotas : cuotasFactura.length,
              // Cada fecha_vencimiento la fijó el usuario al armar el plan → toda cuota es un
              // override → pill verde (mismo criterio que plazopago en la rama sin-cuotas).
              tiene_override: true,
              // Cuota comprometida ya vencida → texto de fecha en rojo (reusa el flag `vencida`
              // del render, mismo criterio string que la rama sin-cuotas en línea ~1697).
              vencida: c.fecha_vencimiento < hoy,
            });
          }
        } else {
          // Fecha de pago: `plazopago` si fue planificado (PlanCuotasModal graba ahí), con
          // fallback a `fecha_emision`. Mismo patrón que las externas (Fuente 2, línea ~1713).
          // `tiene_override` enciende la pill verde; `vencida` usa la fecha EFECTIVA (no la de
          // emisión) para no marcar en rojo una factura con pago planificado a futuro — mismo
          // criterio que la rama "Por cobrar" (plazocobro, línea ~1502).
          const fechaEfectiva = f.plazopago || f.fecha_emision || null;
          provsEntries.push({
            ...f,
            fecha_display: fechaEfectiva,
            importeEnMoneda: -aDisplay(saldoFIFO, monedaLocal, f.fecha_emision),
            moneda_origen: monedaLocal,
            razon_social_receptor: f.razon_social_emisor,
            tipo: "proveedor",
            folio: f.folio,
            tiene_override: !!f.plazopago,
            vencida: !!fechaEfectiva && fechaEfectiva < hoy,
            _parcial: saldoFIFO < Number(f.monto_total || 0),
            _montoOriginal: Number(f.monto_total || 0),
          });
        }
      }
    }

    // Fuente 2 — facturas_externas (asesorías, gastos varios sin DTE SII). Tienen moneda
    // explícita (CLP/PEN/USD). Fecha de pago: plazopago si fue planificado; si no, fallback
    // al día 30 del mes de fecha_emision (mismo criterio de día que las recurrentes, ver
    // proveedoresRecurrentes.js). Excluye las ya vinculadas a un movimiento bancario.
    // Día 30 del mes de una fecha YYYY-MM-DD; si el mes no llega al 30, último día del mes.
    const dia30DelMes = (fechaStr) => {
      if (!fechaStr) return null;
      const [yy, mm] = fechaStr.split("-").map(Number);
      if (!yy || !mm) return null;
      const d = new Date(yy, mm - 1, 30);
      if (d.getMonth() !== mm - 1) d.setDate(0);
      return d.toISOString().split("T")[0];
    };
    for (const f of facturasExternasPendientes) {
      if (f.estado === "pagada") continue;
      if (yaVinculadasExternas.has(f.id)) continue;
      // Excluir filas sin nombre identificable — el display usa f.proveedor_nombre y
      // cuando es falsy queda "—", basura visual sin valor operativo.
      if (!f.proveedor_nombre) continue;
      const esUSD = f.moneda === "USD";
      if (esUSD ? !incluirUSD : !incluirLocal) continue;
      let importe;
      // Moneda real en que está expresado `importe`. Cuando viene del ledger (saldosPorFacturaMap),
      // está en la moneda del proveedor (provMoneda = moneda_default), que puede diferir de f.moneda
      // si el dato es inconsistente. Pasar f.moneda acá deformaría el monto (provMoneda CLP + label USD).
      let monedaImporte;
      if (f.proveedor_id) {
        // Ausente del map = el ledger la dio por pagada → ocultar (mismo criterio que SII).
        // El caso desvincular post-load se cubre inyectando su monto al map en handleMovUpdated.
        const saldoRest = saldosPorFacturaMap.get(`EXT:${f.id}`);
        if (saldoRest === undefined) continue;
        importe = saldoRest;
        monedaImporte = provMonedaMap.get(f.proveedor_id) || f.moneda || monedaLocal;
      } else {
        importe = Number(f.monto_total || 0);
        monedaImporte = f.moneda || monedaLocal;
      }
      provsEntries.push({
        ...f,
        fecha_display: f.plazopago || dia30DelMes(f.fecha_emision) || null,
        importeEnMoneda: -aDisplay(importe, monedaImporte, f.fecha_emision),
        moneda_origen: monedaImporte,
        // Concepto completo (mismo campo `f.concepto` que muestra EstadoCuenta en el ledger, ver
        // ledgerProveedor.js débitos EXT) → no divergen. Ej. OnSign: "OnSign 2026/07 · 187 licencias".
        // Fallback a proveedor_nombre si una externa vieja no tuviera concepto. NO aplica a las
        // proyecciones recurrentes (otra fuente, con su propio formato "(N licencias)").
        razon_social_receptor: f.concepto || f.proveedor_nombre || "—",
        tipo: "proveedor",
        folio: f.numero_documento || null,
        es_externa: true,
        // Pill de override (patrón Backlog, regla 10.89): la fecha vino de plazopago planificado.
        tiene_override: !!f.plazopago,
      });
    }

    // Fuente 3 — residuos por proveedor: deuda del ledger global no atribuible a factura
    // específica (cobros a cuenta, saldo inicial). Hace que la suma del panel coincida
    // con el saldo del estado de cuenta. saldoDisplay viene en provMoneda; respetar el
    // filtro USD/local con la moneda del residuo. fecha_display=hoy: si fuera null,
    // dentroDeCorte() lo filtra cuando hay corte temporal activo (default "Año").
    for (const r of residuosProveedoresPendientes) {
      const esUSD = r.moneda === "USD";
      if (esUSD ? !incluirUSD : !incluirLocal) continue;
      provsEntries.push({
        id: `residuo-${r.provId}`,
        fecha_display: hoy,
        importeEnMoneda: -aDisplay(r.saldoDisplay, r.moneda || monedaLocal, hoy),
        moneda_origen: r.moneda || monedaLocal,
        razon_social_receptor: r.nombre,
        tipo: "proveedor",
        folio: null,
        es_residuo: true,
      });
    }

    // Recurrentes: pueden ser USD o moneda local. importe ya está en su moneda.
    const provsRecurrentesFiltrados = proveedoresRecurrentes
      .filter((p) => (p.moneda_recurrente === "USD" ? incluirUSD : incluirLocal))
      .map((p) => ({
        ...p,
        importeEnMoneda: -aDisplay(
          Math.abs(Number(p.importeEnMoneda || 0)),
          p.moneda_recurrente || monedaLocal,
          p.fecha_display
        ),
        moneda_origen: p.moneda_recurrente === "USD" ? "USD" : monedaLocal,
      }));
    const provsFiltrados = mostrarProveedores
      ? [
          ...(mostrarProveedoresFact ? provsEntries : []),
          ...(mostrarProveedoresRecurrentes ? provsRecurrentesFiltrados : []),
        ]
      : [];

    // RRHH e impuestos siempre se calculan en moneda local. Si no incluyo local, no van.
    const reexpresarLocal = (e) => ({
      ...e,
      importeEnMoneda: -aDisplay(Math.abs(Number(e.importeEnMoneda || 0)), monedaLocal, e.fecha_display),
      moneda_origen: monedaLocal,
    });
    const rrhh = mostrarRRHH && incluirLocal ? rrhhEntries.map(reexpresarLocal) : [];
    const impuestos = mostrarImpuestos && incluirLocal ? impuestosEntries.map(reexpresarLocal) : [];

    const mostrarCapex = mostrarTodo || filtrosActivos.includes("capex");
    const mapCapex = (a) => {
      const monedaOrig = a.moneda === "USD" ? "USD" : monedaLocal;
      return {
        id: `capex-${a.id}`,
        // Id real del activo (el `id` de arriba es la key visual con prefijo) — lo usa el
        // modal "pago ejecutado" (lápiz FaEdit) para actualizarActivo(activo_id, { estado: ejecutado_sin_factura }).
        activo_id: a.id,
        fecha_display: a.fecha_planificada,
        // Planificado vencido → fecha en rojo (mismo criterio que Backlog/cuotas). El color lo
        // aplica la regla "vencida universal" del render; el flag se setea por consistencia.
        vencida: !!a.fecha_planificada && a.fecha_planificada < hoy,
        importeEnMoneda: -aDisplay(Number(a.costo_unitario), monedaOrig, a.fecha_planificada),
        moneda_origen: monedaOrig,
        razon_social_receptor: [
          a.destino_id ? edificiosMap[a.destino_id] || `Edificio #${a.destino_id}` : null,
          a.tipo_activo?.toUpperCase(),
          a.proveedor?.nombre || null,
        ]
          .filter(Boolean)
          .join(" | "),
        tipo: "capex",
      };
    };
    // Línea de LOTE de compra (bloque Stock). Mapper propio — NO se toca el mapCapex
    // legacy de arriba (su fix de `cantidad` es Fase 3). Diferencia clave: acá el monto
    // SÍ multiplica por la cantidad, porque un lote es N unidades en un solo egreso.
    const mapCapexLote = (l) => {
      const monedaOrig = l.moneda === "USD" ? "USD" : monedaLocal;
      const total = Number(l.costo_unitario || 0) * Number(l.cantidad_total || 0);
      return {
        id: `capex-lote-${l.id}`,
        lote_id: l.id,
        // Marca que esta fila es un lote: la excluye del lápiz "marcar pagado" (que
        // espera un activo_id). Ejecutar un lote es sub-fase 5, desde Activos.
        es_lote: true,
        fecha_display: l.fecha_planificada,
        // Mismo criterio de vencida que los activos planificados (regla 10.159): un
        // CapEx comprometido y vencido no se oculta, se muestra en rojo.
        vencida: !!l.fecha_planificada && l.fecha_planificada < hoy,
        importeEnMoneda: -aDisplay(total, monedaOrig, l.fecha_planificada),
        moneda_origen: monedaOrig,
        razon_social_receptor: [
          `STOCK ${l.cantidad_total}x`,
          l.tipo_activo?.toUpperCase(),
          l.proveedor_rel?.nombre || l.proveedor || null,
        ]
          .filter(Boolean)
          .join(" | "),
        tipo: "capex",
      };
    };

    // Línea de COMPONENTE de costo (landed cost) de un lote (9.36). SUMA a la línea FOB del lote
    // (mapCapexLote), no la reemplaza: el FOB es la base y estos son extras (transporte/aduana/otro)
    // que capitalizan aparte, cada uno con su propia moneda y fecha_pago → no hay doble conteo ni
    // bifurcación tipo cuotas. Misma razon_social que su lote para que se agrupen visualmente; el tipo
    // del componente va en FOLIO para distinguirla de la FOB. `es_lote: true` la deja fuera del kebab
    // (igual que la línea FOB); la conciliación es el commit 4.
    const TIPO_COMPONENTE_LABEL = { transporte: "Transporte", aduana: "Aduana", otro: "Otro", fob: "FOB" };
    const mapCapexComponente = (c) => {
      const l = c.lote || {};
      const monedaOrig = c.moneda === "USD" ? "USD" : monedaLocal;
      return {
        id: `capex-componente-${c.id}`,
        lote_id: c.lote_id,
        es_lote: true,
        componente_tipo: c.tipo,
        fecha_display: c.fecha_pago,
        vencida: !!c.fecha_pago && c.fecha_pago < hoy,
        importeEnMoneda: -aDisplay(Number(c.monto || 0), monedaOrig, c.fecha_pago),
        moneda_origen: monedaOrig,
        razon_social_receptor: [`STOCK ${l.cantidad_total}x`, l.tipo_activo?.toUpperCase(), l.proveedor?.nombre || null]
          .filter(Boolean)
          .join(" | "),
        folio: TIPO_COMPONENTE_LABEL[c.tipo] || c.tipo,
        tipo: "capex",
      };
    };

    // Índice de cuotas pendientes por activo. Un activo CON plan proyecta una línea por cuota
    // (fecha_vencimiento/monto_cuota) en vez de la línea única de mapCapex — nunca las dos fuentes a
    // la vez (única fuente de la verdad, §7). Caja pura: no toca estado/fecha_compra/depreciación.
    const cuotasActivoPorId = {};
    for (const c of cuotasActivosPendientes) {
      if (!cuotasActivoPorId[c.activo_id]) cuotasActivoPorId[c.activo_id] = [];
      cuotasActivoPorId[c.activo_id].push(c);
    }
    const mapCuotaActivo = (a, c) => {
      const monedaOrig = a.moneda === "USD" ? "USD" : monedaLocal;
      return {
        id: `capex-cuota-${c.id}`,
        activo_id: a.id,
        tiene_plan: true, // marca la fila como "cuota de plan" para el kebab por tipo de línea
        fecha_display: c.fecha_vencimiento,
        vencida: !!c.fecha_vencimiento && c.fecha_vencimiento < hoy,
        importeEnMoneda: -aDisplay(Number(c.monto_cuota), monedaOrig, c.fecha_vencimiento),
        moneda_origen: monedaOrig,
        razon_social_receptor: [
          a.destino_id ? edificiosMap[a.destino_id] || `Edificio #${a.destino_id}` : null,
          a.tipo_activo?.toUpperCase(),
          a.proveedor?.nombre || null,
        ]
          .filter(Boolean)
          .join(" | "),
        // "Cuota X/N" en la columna FOLIO (mismo patrón que facturas/créditos en cuotas), no en el
        // concepto, para no romper el layout.
        folio: "Cuota",
        cuota_actual: c.numero_cuota,
        total_cuotas: c.total_cuotas > 0 ? c.total_cuotas : (cuotasActivoPorId[a.id] || []).length,
        tipo: "capex",
      };
    };

    // Unificado con el modal "Compra de activo" (VincularMovimientoModal): SIN filtro de fecha.
    // Un planificado vencido NO se oculta: se muestra en su fecha comprometida, en rojo (regla
    // vencida universal), hasta que se ejecute (vinculando banco) o se elimine — igual que
    // Backlog/cuotas. Se exige fecha_planificada no-null (el alta la blinda; 0 legacy sin fecha).
    const capexFiltrado = capexEntries
      .filter((a) => a.fecha_planificada)
      .filter((a) => (a.moneda === "USD" ? incluirUSD : incluirLocal));
    // Misma regla para los lotes: se exige fecha_planificada (el alta la blinda) y se
    // respeta el filtro de moneda de la vista.
    const capexLotesFiltrado = capexLotes
      .filter((l) => l.fecha_planificada)
      .filter((l) => (l.moneda === "USD" ? incluirUSD : incluirLocal));
    // Componentes de costo: exigen fecha_pago (NOT NULL en DB, siempre presente) y respetan el filtro
    // de moneda de la vista según la moneda propia del componente (que puede diferir de la del lote).
    const componentesFiltrado = componentesLotes
      .filter((c) => c.fecha_pago)
      .filter((c) => (c.moneda === "USD" ? incluirUSD : incluirLocal));
    // Bifurcación por activo: CON plan → una línea por cuota pendiente (mapCuotaActivo); si el plan
    // está completo (sin pendientes) no emite ninguna línea, nunca cae a la única. SIN plan → mapCapex.
    // Lotes: línea FOB (mapCapexLote) + una línea por componente pendiente (mapCapexComponente). El
    // FOB y los componentes son plata distinta → suman, no se reemplazan (corrige §7 del informe).
    const capex = mostrarCapex
      ? [
          ...capexFiltrado.flatMap((a) =>
            activosConPlan.has(a.id) ? (cuotasActivoPorId[a.id] || []).map((c) => mapCuotaActivo(a, c)) : [mapCapex(a)]
          ),
          ...capexLotesFiltrado.map(mapCapexLote),
          ...componentesFiltrado.map(mapCapexComponente),
        ]
      : [];

    // Edificios: si hay pagos planificados en plan_pagos_edificios, se genera una entry
    // por cada plan + (opcional) una fila residual con el saldo no planificado en la
    // próxima fecha trimestral. Si no hay planes, una sola entry con el saldo total.
    // Vienen siempre en moneda local.
    const mostrarEdificios = mostrarTodo || filtrosActivos.includes("edificios");
    const edificiosEntries = [];
    if (mostrarEdificios && incluirLocal) {
      for (const e of edificiosSaldo) {
        const plansEdificio = planPagosEdificios.filter((p) => p.edificio_id === e.edificioId);
        if (plansEdificio.length > 0) {
          for (const plan of plansEdificio) {
            edificiosEntries.push({
              id: `edificio-plan-${plan.id}`,
              fecha_display: plan.fecha_acordada,
              importeEnMoneda: -aDisplay(Number(plan.monto || 0), monedaLocal, plan.fecha_acordada),
              moneda_origen: e.moneda,
              razon_social_receptor: e.edificioNombre,
              tipo: "edificio",
              folio: plan.nota || null,
              edificioData: e,
              planId: plan.id,
            });
          }
          const totalPlanificado = plansEdificio.reduce((sum, p) => sum + Number(p.monto || 0), 0);
          const saldoRestante = Math.round(Number(e.saldoPendiente || 0) - totalPlanificado);
          if (saldoRestante > 0) {
            edificiosEntries.push({
              id: `edificio-residual-${e.edificioId}`,
              fecha_display: e.proximaFecha,
              importeEnMoneda: -aDisplay(saldoRestante, monedaLocal, e.proximaFecha),
              moneda_origen: e.moneda,
              razon_social_receptor: e.edificioNombre,
              tipo: "edificio",
              folio: null,
              edificioData: e,
            });
          }
        } else {
          edificiosEntries.push({
            id: `edificio-${e.edificioId}`,
            fecha_display: e.proximaFecha,
            importeEnMoneda: -aDisplay(e.saldoPendiente, monedaLocal, e.proximaFecha),
            moneda_origen: e.moneda,
            razon_social_receptor: e.edificioNombre,
            tipo: "edificio",
            folio: null,
            edificioData: e,
          });
        }
      }
    }

    // Tab "financiamiento" (label "Pasivo"): alimenta dos fuentes — moras de factoring
    // (creditosEntries, mapMora, siempre en moneda local) y cuotas de crédito bancario
    // (cuotasCreditoEntries, de creditos_cuotas, más abajo).
    const mostrarCreditos = mostrarTodo || filtrosActivos.includes("financiamiento");
    const mapMora = (m) => ({
      id: `mora-${m.id}`,
      fecha_display: m.fecha_notificacion,
      importeEnMoneda: -aDisplay(Number(m.monto_mora || 0), monedaLocal, m.fecha_notificacion),
      moneda_origen: monedaLocal,
      razon_social_receptor: [
        "Mora factoring",
        m.operacion?.factura?.folio ? `Folio ${m.operacion.factura.folio}` : null,
        m.operacion?.factura?.razon_social_receptor || null,
      ]
        .filter(Boolean)
        .join(" — "),
      tipo: "credito",
      folio: null,
    });
    const creditosEntries = mostrarCreditos && incluirLocal ? morasPendientes.map(mapMora) : [];

    // Créditos bancarios: cuotas pendientes de creditos_cuotas (JOIN creditos activos). Cada
    // cuota se proyecta como egreso en su fecha de vencimiento. La moneda es la del crédito
    // (CLP/PEN o USD) → filtrado por la vista y reexpresado al display con aDisplay.
    const mapCuotaCredito = (c) => {
      const monedaCredito = c.creditos?.moneda || monedaLocal;
      return {
        id: `credito-cuota-${c.id}`,
        fecha_display: c.fecha_vencimiento,
        razon_social_receptor: c.creditos?.nombre || "Crédito",
        folio: `Cuota ${c.numero_cuota}`,
        importeEnMoneda: -aDisplay(Number(c.monto_total || 0), monedaCredito, c.fecha_vencimiento),
        moneda_origen: monedaCredito,
        tipo: "credito_cuota",
        es_credito_cuota: true,
        vencida: c.fecha_vencimiento < hoy,
      };
    };
    const cuotasCreditoEntries = mostrarCreditos
      ? cuotasCreditos.filter((c) => (c.creditos?.moneda === "USD" ? incluirUSD : incluirLocal)).map(mapCuotaCredito)
      : [];

    let todos = [
      ...filtrarPorCorte(facturadoEntries),
      ...filtrarPorCorte(factoringEntries).filter((e) => (e.moneda_dolar_origen ? incluirUSD : incluirLocal)),
      ...filtrarPorCorte(pipe),
      ...filtrarPorCorte(provsFiltrados),
      ...filtrarPorCorte(rrhh),
      ...filtrarPorCorte(impuestos),
      ...filtrarPorCorte(capex),
      ...filtrarPorCorte(edificiosEntries),
      ...filtrarPorCorte(creditosEntries),
      ...filtrarPorCorte(cuotasCreditoEntries),
    ].sort((a, b) => {
      // Nivel 1: fecha de pago/cobro proyectada asc (null al final).
      const porFecha = (a.fecha_display || "9999").localeCompare(b.fecha_display || "9999");
      if (porFecha !== 0) return porFecha;
      // Desempate SOLO entre facturas (ambas con fecha_emision). Proyecciones y cruces
      // factura↔proyección quedan con el orden estable actual (retorno 0 → insertion order).
      if (!a.fecha_emision || !b.fecha_emision) return 0;
      // Nivel 2: fecha de emisión asc → la más antigua primero (ej. OnSign mayo antes que junio).
      const porEmision = String(a.fecha_emision).localeCompare(String(b.fecha_emision));
      if (porEmision !== 0) return porEmision;
      // Nivel 3: importe con signo asc (mismo campo en las 3 clases), solo si ambas lo traen.
      if (a.importeEnMoneda == null || b.importeEnMoneda == null) return 0;
      return a.importeEnMoneda - b.importeEnMoneda;
    });

    // Búsqueda libre: nombre (razon_social_receptor), folio o monto absoluto redondeado.
    // Aplica DESPUÉS de filtrosActivos/corte temporal, antes del cálculo de saldo —
    // ojo que el saldo acumulado refleja sólo lo visible (mismo trade-off que el histórico).
    if (busquedaFuturos.trim()) {
      // Normalizar diacríticos para que "itau" matchee "ITAÚ" (y viceversa). Solo aplica al
      // nombre; folio y monto quedan igual (no llevan acentos).
      const sinAcentos = (s) =>
        (s || "")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
      const q = sinAcentos(busquedaFuturos.trim());
      todos = todos.filter(
        (m) =>
          sinAcentos(m.razon_social_receptor).includes(q) ||
          String(m.folio || "")
            .toLowerCase()
            .includes(q) ||
          String(Math.abs(Math.round(Number(m.importeEnMoneda) || 0))).includes(q)
      );
    }

    // Saldo proyectado parte del saldo actual (ya en monedaDisplay).
    let saldo = saldoActual;
    return todos.map((f) => {
      saldo += f.importeEnMoneda;
      return { ...f, saldo_proyectado: saldo };
    });
  }, [
    pendientesCobro,
    pipeline,
    proveedoresPendientes,
    facturaSiiPorPagar,
    proveedoresRecurrentes,
    facturasExternasPendientes,
    saldosPorFacturaMap,
    provMonedaMap,
    residuosProveedoresPendientes,
    yaVinculadasExternas,
    yaVinculadasEmitidas,
    cuotasPagosPendientes,
    cuotasCobrosPendientes,
    cuotasActivosPendientes,
    activosConPlan,
    rrhhEntries,
    impuestosEntries,
    capexEntries,
    capexLotes,
    componentesLotes,
    edificiosMap,
    edificiosSaldo,
    planPagosEdificios,
    morasPendientes,
    cuotasCreditos,
    operacionesFactoring,
    filtrosActivos,
    filtroTemporalKey,
    monedaVista,
    monedaLocal,
    monedaDisplay,
    consolidado,
    saldoActual,
    convertir,
    busquedaFuturos,
  ]);

  const fmt = (n) =>
    monedaDisplay === "USD"
      ? `US$ ${Number(n).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`
      : `${simboloLocal} ${Number(n).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;

  // Badge pill con el código corto de la moneda origen. Azul claro para USD, naranja/beige
  // para local. Se usa en la columna "Ref" cuando consolidado está activo.
  const renderBadgeMoneda = (monedaOrigen) => {
    if (!monedaOrigen) return null;
    const esUSD = monedaOrigen === "USD";
    const txt = esUSD ? "US$" : monedaLocal;
    return (
      <span
        style={{
          display: "inline-block",
          padding: "1px 6px",
          fontSize: "0.65rem",
          fontWeight: 700,
          letterSpacing: "0.04em",
          borderRadius: 3,
          background: esUSD ? "#e8f4f8" : "#fef5e7",
          color: esUSD ? "#2980b9" : "#d35400",
          border: `1px solid ${esUSD ? "#aed6f1" : "#fdebd0"}`,
          lineHeight: "14px",
        }}
      >
        {txt}
      </span>
    );
  };

  // Borde lateral izquierdo en filas cuya moneda origen difiere de la de display.
  // Sólo aplica en consolidado (cuando hay mezcla de orígenes posible).
  const bordeMonedaStyle = (monedaOrigen) => {
    if (!consolidado || !monedaOrigen || monedaOrigen === monedaDisplay) return undefined;
    const color = monedaOrigen === "USD" ? "#3498db" : "#e67e22";
    return { boxShadow: `inset 3px 0 0 ${color}` };
  };

  // Neto de las filas visibles del panel (sin el saldo de caja): Σ importeEnMoneda
  // de la proyección ya filtrada por tabs/corte/búsqueda. Cada fila trae el signo
  // baked-in (cobrar +, pagar −), así que el reduce da el flujo neto de lo mostrado.
  // A diferencia del saldo_proyectado de las filas, NO parte del saldo bancario actual.
  const netoVisibleFuturos = useMemo(
    () => proyeccionesFuturas.reduce((acc, f) => acc + (Number(f.importeEnMoneda) || 0), 0),
    [proyeccionesFuturas]
  );

  // Suma neta de los movimientos históricos visibles (ya filtrados por buscador + tab
  // Ingresos/Egresos + filtro temporal). Mismo criterio de signo que netoVisibleFuturos:
  // ingreso suma +, egreso resta −. Usa monto_display (moneda activa CLP/USD). Reacciona
  // en vivo al filtro porque depende de movimientosFiltrados. Reemplaza al contador de la
  // barra (la cantidad ya vive en el badge del toggle CLP/USD del header).
  const netoVisibleHistorico = useMemo(
    () =>
      movimientosFiltrados.reduce(
        (acc, m) => acc + (m.tipo === "ingreso" ? Number(m.monto_display || 0) : -Number(m.monto_display || 0)),
        0
      ),
    [movimientosFiltrados]
  );

  // El neto de cada barra se muestra SOLO con filtro activo (sirve para cuadrar lo visible);
  // sin filtro queda vacío. Históricos: default filtroHistorico='todo', filtroTiempo='ytd'.
  const hayFiltroHistorico =
    busquedaHistorico.trim() !== "" || filtroHistorico !== "todo" || filtroTiempo !== "ytd" || soloPendientes;
  // Futuros: default filtrosActivos=['todo'] (tab "Todo"), sin corte temporal, sin búsqueda.
  const hayFiltroFuturos =
    busquedaFuturos.trim() !== "" || !filtrosActivos.includes("todo") || filtroTemporalKey !== null;

  // Tab Backlog en exclusiva: la columna FOLIO pasa a REFERENCIA (identifica la venta/cuota
  // de origen, que aún no tiene folio SII). En cualquier otro tab sigue siendo FOLIO.
  const soloBacklog = filtrosActivos.length === 1 && filtrosActivos[0] === "backlog";
  // "MM-YYYY" → "MM/YY". El mes sale del dato real de la entry (no de hoy).
  const mmYY = (mmYYYY) => {
    if (!mmYYYY || typeof mmYYYY !== "string") return "";
    const [mm, yyyy] = mmYYYY.split("-");
    return mm && yyyy ? `${mm}/${yyyy.slice(-2)}` : "";
  };
  // Referencia de una entry del Backlog según su origen (regla 10.89: la metadata ya viaja).
  const refBacklog = (f) => {
    if (f.origen === "sales") return `#${f.sale_id}`;
    if (f.origen === "contratos") {
      const m = mmYY(f.mes);
      return m ? `C-${f.contrato_id} · ${m}` : `C-${f.contrato_id}`;
    }
    if (f.origen === "carteleria") {
      // fecha_estimada de cartelería = último día del mes de servicio ("YYYY-MM-DD").
      const [yyyy, mm] = (f.fecha_estimada || "").split("-");
      return yyyy && mm ? `${mm}/${yyyy.slice(-2)}` : "—";
    }
    return "—";
  };

  // Cuenta facturas SII DISTINTAS que el panel "Por pagar" muestra (Fuente 1), usando el mismo
  // predicado que el render → badge y filas no pueden divergir por construcción (regla 10.1).
  // Mide facturas, no filas: las cuotas de una factura NO multiplican el conteo.
  const countPorPagarPendientes = useMemo(
    () => proveedoresPendientes.filter(facturaSiiPorPagar).length,
    [proveedoresPendientes, facturaSiiPorPagar]
  );

  const gridRef = useRef(null);

  // Fuente única del estado "pendiente de vincular": mismo predicado isMovPendiente
  // y mismos sets (pagosMovIds + movimientosBancarioFactoring) que pintan la fila de
  // naranja en la tabla histórica (ver invocación de fila). Así el badge nunca diverge.
  const movPendientes = movBancarios.filter((m) => isMovPendiente(m, pagosMovIds, movimientosBancarioFactoring));
  const pendientesPorMoneda = movPendientes.reduce((acc, m) => {
    const mon = m.moneda || monedaLocal;
    acc[mon] = (acc[mon] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className={styles.wrapper}>
      <Header
        leftContent={
          <div style={{ display: "flex", alignItems: "center", gap: 32, marginLeft: 150 }}>
            <div
              style={{
                display: "flex",
                gap: 0,
                borderRadius: 6,
                overflow: "hidden",
                border: "1px solid #dee2e6",
              }}
            >
              {[monedaLocal, "USD"].map((m, idx) => {
                const pend = pendientesPorMoneda[m] || 0;
                const activo = monedaVista === m;
                const badgeFirst = idx === 0;
                const badge = pend > 0 && (
                  <span
                    style={{
                      background: "#c62828",
                      color: "#fff",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      minWidth: 18,
                      height: 18,
                      borderRadius: 9,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0 5px",
                    }}
                  >
                    {pend}
                  </span>
                );
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMonedaVista(m)}
                    title={
                      pend > 0
                        ? `${pend} movimiento${pend === 1 ? "" : "s"} pendiente${pend === 1 ? "" : "s"} de vincular`
                        : undefined
                    }
                    style={{
                      width: 108,
                      padding: "6px 0",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      border: "none",
                      cursor: "pointer",
                      background: activo ? "#2c3e50" : "transparent",
                      color: activo ? "#fff" : "#666",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    {badgeFirst ? badge : null}
                    {m}
                    {badgeFirst ? null : badge}
                  </button>
                );
              })}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                fontSize: "0.75rem",
                color: "#444",
                userSelect: "none",
              }}
            >
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", margin: 0 }}>
                <input
                  type="checkbox"
                  checked={consolidado}
                  onChange={(e) => setConsolidado(e.target.checked)}
                  style={{ cursor: "pointer" }}
                />
                Consolidado
              </label>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: monedaVista === "USD" ? "not-allowed" : "pointer",
                  margin: 0,
                  opacity: monedaVista === "USD" ? 0.4 : 1,
                }}
                title={monedaVista === "USD" ? "Ya estás viendo en USD" : undefined}
              >
                <input
                  type="checkbox"
                  checked={monedaVista === "USD" ? false : convertirAUSD}
                  disabled={monedaVista === "USD"}
                  onChange={(e) => setConvertirAUSD(e.target.checked)}
                  style={{ cursor: monedaVista === "USD" ? "not-allowed" : "pointer" }}
                />
                Ver en US$
              </label>
            </div>
          </div>
        }
      >
        <div className={styles.headerStats}>
          <div className={styles.statItem}>
            <div className={styles.statLabel}>Saldo actual</div>
            <div className={styles.statValue}>{fmt(saldoActual)}</div>
          </div>
          <button
            type="button"
            onClick={handleSyncBanco}
            disabled={sincronizandoBanco || sincronizandoSII}
            style={{
              width: 150,
              padding: "6px 0",
              fontSize: "0.78rem",
              fontWeight: 600,
              border: "1px solid #2c3e50",
              borderRadius: 6,
              background: sincronizandoBanco ? "#95a5a6" : "#2c3e50",
              color: "#fff",
              cursor: sincronizandoBanco || sincronizandoSII ? "not-allowed" : "pointer",
              marginLeft: 12,
            }}
          >
            {sincronizandoBanco ? "Actualizando..." : "Actualizar banco"}
          </button>
          <button
            type="button"
            onClick={handleSyncSII}
            disabled={sincronizandoBanco || sincronizandoSII}
            style={{
              width: 150,
              padding: "6px 0",
              fontSize: "0.78rem",
              fontWeight: 600,
              border: "1px solid #6c3483",
              borderRadius: 6,
              background: sincronizandoSII ? "#95a5a6" : "#6c3483",
              color: "#fff",
              cursor: sincronizandoBanco || sincronizandoSII ? "not-allowed" : "pointer",
              marginLeft: 8,
            }}
          >
            {sincronizandoSII ? "Actualizando..." : `Actualizar ${enteFiscal}`}
          </button>
        </div>
      </Header>

      <div className={styles.content}>
        <div className={styles.grid} ref={gridRef}>
          <div className={styles.connector} />

          {/* Panel izquierdo */}
          <div className={styles.panelWrapper}>
            <div
              className={styles.panelHeader}
              style={{
                background: "#2c3e50",
                display: "flex",
                alignItems: "center",
                position: "relative",
              }}
            >
              <span>Movimientos históricos{ultimaConsulta ? " |" : ""}</span>
              {ultimaConsulta && (
                <span className={styles.ultimaSync}>Última carga: {format(ultimaConsulta, "dd/MM · HH:mm")}</span>
              )}
              <input
                type="text"
                placeholder="Buscar movimiento..."
                value={busquedaHistorico}
                onChange={(e) => setBusquedaHistorico(e.target.value)}
                className={styles.searchInputDark}
              />
              {hayFiltroHistorico && (
                <span
                  style={{ fontSize: "0.85rem", fontWeight: 700, opacity: 0.95, color: "#fff", marginLeft: "auto" }}
                >
                  {fmt(netoVisibleHistorico)}
                </span>
              )}
            </div>
            <div
              className={styles.filterBar}
              style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}
            >
              <div style={{ display: "flex", gap: 4 }}>
                {FILTROS_HISTORICO.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFiltroHistorico(f.key)}
                    className={`${styles.filterBtn} ${filtroHistorico === f.key ? styles.filterBtnActive : ""}`}
                  >
                    {f.label}
                  </button>
                ))}
                {/* Toggle independiente (soloPendientes), NO un valor de filtroHistorico.
                    4º botón del grupo solo por posición; su lógica es combinable. */}
                <button
                  type="button"
                  onClick={() => setSoloPendientes((v) => !v)}
                  className={`${styles.filterBtn} ${soloPendientes ? styles.filterBtnActive : ""}`}
                >
                  Pendiente
                </button>
              </div>
              {/* Toggle de atención (⚠): filtro AND independiente, centrado entre los dos grupos.
                  Enabled solo si hay forzados pendientes en la vista base (hayAtencionEnVista);
                  si no, gris + not-allowed (el auto-clear ya lo apaga si se queda sin ⚠). */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <button
                  type="button"
                  onClick={() => setSoloAtencion((v) => !v)}
                  disabled={!hayAtencionEnVista}
                  title={
                    hayAtencionEnVista
                      ? "Mostrar solo movimientos con forzado pendiente"
                      : "Sin forzados pendientes en la vista actual"
                  }
                  className={`${styles.filterBtn} ${soloAtencion ? styles.filterBtnActive : ""}`}
                  style={{
                    // Solo tiene el ícono: cuadradito compacto. Achica ancho (min-width + padding
                    // horizontal) SIN tocar el padding vertical (5px de filterBtn) → alto intacto.
                    minWidth: 0,
                    paddingLeft: 7,
                    paddingRight: 7,
                    ...(!hayAtencionEnVista ? { cursor: "not-allowed", opacity: 0.45 } : {}),
                  }}
                >
                  <span
                    style={{
                      color: hayAtencionEnVista ? "#e67e22" : "#adb5bd",
                      fontSize: 15,
                      lineHeight: 1,
                      fontWeight: 700,
                      display: "inline-block",
                      verticalAlign: "middle",
                    }}
                  >
                    {"⚠︎"}
                  </span>
                </button>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                {[
                  { key: "hoy", label: "Hoy" },
                  { key: "esta_semana", label: "Semana" },
                  { key: "este_mes", label: "Mes" },
                  { key: "ytd", label: "YTD" },
                ].map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFiltroTiempo(f.key)}
                    className={`${styles.filterBtn} ${filtroTiempo === f.key ? styles.filterBtnActive : ""}`}
                  >
                    {f.label}
                  </button>
                ))}
                <select
                  value={filtroTiempo.startsWith("mes_") ? filtroTiempo : ""}
                  onChange={(e) => e.target.value && setFiltroTiempo(e.target.value)}
                  style={{
                    padding: "5px 8px",
                    fontSize: "0.78rem",
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    background: filtroTiempo.startsWith("mes_") ? "#2c3e50" : "#fff",
                    color: filtroTiempo.startsWith("mes_") ? "#fff" : "#333",
                    cursor: "pointer",
                  }}
                >
                  <option value="">Seleccionar…</option>
                  {mesesDropdown.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.panel} style={{ borderRadius: "0 0 6px 6px" }}>
              {loading ? (
                <div className={styles.empty}>Cargando...</div>
              ) : movimientosFiltrados.length === 0 ? (
                <div className={styles.empty}>Sin movimientos</div>
              ) : (
                <table className={`${styles.table} ${styles.tableHistorico}`}>
                  <colgroup>
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "44%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "7%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th colSpan={2}>Movimiento</th>
                      <th>Monto</th>
                      <th>Saldo</th>
                      <th>{consolidado ? "Ref" : "Edit"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimientosFiltrados.map((m) => (
                      <tr
                        key={m.id}
                        className={isMovPendiente(m, pagosMovIds, movimientosBancarioFactoring) ? styles.pendiente : ""}
                      >
                        <td className={styles.fecha} style={bordeMonedaStyle(m.moneda_origen)}>
                          {formatFecha(m.fecha)}
                        </td>
                        <td className={styles.folio}>
                          {renderBadgeMovimiento(
                            m,
                            pagosMovIds,
                            enteFiscal,
                            movimientosBancarioFactoring,
                            mapaFactoring,
                            facturaInfoMap,
                            facturaRecibidaInfoMap
                          )}
                        </td>
                        <td className={styles.descripcion} title={m.descripcion_raw} style={{ position: "relative" }}>
                          {m.descripcion_raw}
                          {renderMarcaForzado(m, forzadosMap)}
                        </td>
                        <td className={`${styles.right} ${m.tipo === "ingreso" ? styles.ingreso : styles.egreso}`}>
                          {fmt(m.monto_display ?? m.monto)}
                        </td>
                        <td className={styles.right}>{fmt(m.saldo_calculado)}</td>
                        <td className={styles.accion}>
                          {consolidado ? (
                            renderBadgeMoneda(m.moneda_origen)
                          ) : (
                            <FaEdit
                              title="Vincular"
                              style={{ cursor: "pointer", color: "#999", fontSize: "1em" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setVincularModal(m);
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Panel derecho */}
          <div className={styles.panelWrapper}>
            <div
              className={styles.panelHeader}
              style={{
                background: "#27ae60",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span>Movimientos futuros</span>
              <span aria-hidden="true" style={{ color: "#fff", opacity: 0.4 }}>
                |
              </span>
              <div style={{ display: "flex", gap: 4 }}>
                {calcularFiltrosTemporales().map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => {
                      setFiltroTemporalKey((prev) => (prev === f.key ? null : f.key));
                    }}
                    className={`${styles.bannerBtn} ${filtroTemporalKey === f.key ? styles.bannerBtnActive : ""}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                placeholder="Buscar movimiento..."
                value={busquedaFuturos}
                onChange={(e) => setBusquedaFuturos(e.target.value)}
                className={styles.searchInputLight}
              />
              {hayFiltroFuturos && (
                <span
                  style={{ fontSize: "0.85rem", fontWeight: 700, opacity: 0.95, color: "#fff", marginLeft: "auto" }}
                >
                  {fmt(netoVisibleFuturos)}
                </span>
              )}
            </div>
            <div className={`${styles.filterBar} ${styles.filterBarFull}`}>
              {FILTROS_PROYECCION.map((f) => {
                const btn = (
                  <button
                    type="button"
                    onClick={() => toggleFiltro(f.key)}
                    className={`${styles.filterBtn} ${filtrosActivos.includes(f.key) ? styles.filterBtnActive : ""}`}
                  >
                    {f.label}
                  </button>
                );
                // Solo "Por pagar" lleva badge rojo (pendientes que requieren acción). Las cuotas
                // de crédito del tab "Créditos" son proyecciones informativas → sin badge.
                if (f.key === "proveedores_facturados") {
                  return (
                    <span key={f.key} style={{ position: "relative", display: "inline-block" }}>
                      {btn}
                      {countPorPagarPendientes > 0 && (
                        <span
                          style={{
                            position: "absolute",
                            top: -6,
                            right: -6,
                            background: "#c62828",
                            color: "#fff",
                            fontSize: "0.55rem",
                            fontWeight: 700,
                            minWidth: 14,
                            height: 14,
                            borderRadius: 7,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 3px",
                            pointerEvents: "none",
                            zIndex: 1,
                          }}
                        >
                          {countPorPagarPendientes}
                        </span>
                      )}
                    </span>
                  );
                }
                return <span key={f.key}>{btn}</span>;
              })}
            </div>
            <div className={styles.panel} style={{ borderRadius: "0 0 6px 6px" }}>
              {proyeccionesFuturas.length === 0 ? (
                <div className={styles.empty}>Sin proyecciones</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Concepto</th>
                      <th>{soloBacklog ? "Referencia" : "Folio"}</th>
                      <th>Monto</th>
                      <th>Saldo</th>
                      <th>{consolidado ? "Ref" : "Edit"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proyeccionesFuturas.map((f, i) => (
                      <tr
                        key={`${f.id}-${i}`}
                        style={{ cursor: f.tipo === "facturado" ? "pointer" : "default" }}
                        onClick={() => {
                          if (f.tipo === "facturado") {
                            setPlanCuotasModal({ factura: f, tipo: "cobro", accionInicial: "fecha" });
                          }
                        }}
                      >
                        <td
                          className={`${styles.fecha} ${!f.fecha_display ? styles.sinFecha : ""}`}
                          style={{
                            // Vencida universal: cualquier fecha_display en el pasado va en rojo,
                            // sin depender del flag f.vencida (se setea rama por rama y hay tipos
                            // que no lo calculan, p.ej. recurrentes/factoring). null → no vencida.
                            ...(f.fecha_display && f.fecha_display < hoyStr
                              ? { color: "#e74c3c", fontWeight: 600 }
                              : {}),
                            ...(bordeMonedaStyle(f.moneda_origen) || {}),
                          }}
                        >
                          {f.fecha_display ? (
                            f.tiene_override ? (
                              // Pill bordó si la fecha proyectada ya venció; verde si vigente.
                              <span
                                style={
                                  f.fecha_display < hoyStr
                                    ? {
                                        background: "#f9dedc",
                                        border: "1px solid #c0392b",
                                        borderRadius: "4px",
                                        padding: "2px 6px",
                                        color: "#922b21",
                                      }
                                    : {
                                        background: "#e8f5e9",
                                        border: "1px solid #66bb6a",
                                        borderRadius: "4px",
                                        padding: "2px 6px",
                                      }
                                }
                                title={
                                  f.fecha_display < hoyStr
                                    ? "Fecha proyectada vencida"
                                    : "Fecha de cobro con override manual"
                                }
                              >
                                {formatFecha(f.fecha_display)}
                              </span>
                            ) : (
                              formatFecha(f.fecha_display)
                            )
                          ) : (
                            "Sin fecha"
                          )}
                        </td>
                        <td className={styles.descripcion}>
                          {f.razon_social_receptor || "—"}
                          {f.estimado && (
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: "0.68rem",
                                background: "#f39c12",
                                color: "#fff",
                                borderRadius: 3,
                                padding: "1px 5px",
                              }}
                            >
                              Estimado
                            </span>
                          )}
                          {f._parcial && (
                            <span style={{ marginLeft: 6, color: "#e67e22", fontSize: "0.68rem", fontStyle: "italic" }}>
                              parcial
                            </span>
                          )}
                        </td>
                        <td className={styles.folio}>
                          {soloBacklog ? (
                            refBacklog(f)
                          ) : f.folio ? (
                            f.cuota_actual ? (
                              `${f.folio} (${f.cuota_actual}/${f.total_cuotas})`
                            ) : (
                              `${f.folio}`
                            )
                          ) : f.es_residuo ? (
                            <span style={{ color: "#999" }}>Sin folio</span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td
                          className={`${styles.right} ${
                            f.tipo === "pipeline"
                              ? styles.pipeline
                              : f.importeEnMoneda < 0
                                ? styles.egreso
                                : styles.ingreso
                          }`}
                        >
                          {fmt(Math.abs(f.importeEnMoneda))}
                        </td>
                        <td className={`${styles.right} ${f.saldo_proyectado >= 0 ? styles.ingreso : styles.egreso}`}>
                          {fmt(f.saldo_proyectado)}
                        </td>
                        <td className={styles.accion}>
                          {consolidado ? (
                            renderBadgeMoneda(f.moneda_origen)
                          ) : esEntryEditable(f) && f.tipo === "capex" ? (
                            // CapEx: menú de acciones (planificar fecha de proyección + marcar pagado),
                            // en vez del lápiz único. Los de lote/Stock (es_lote) no pasan esEntryEditable.
                            <KebabMenu
                              minWidth={210}
                              buttonClassName={styles.kebabAccion}
                              items={
                                f.tiene_plan
                                  ? [
                                      // Línea de cuota (activo con plan): solo editar el plan. La
                                      // conciliación por cuota es el commit 4; la fecha de cada cuota
                                      // se edita dentro del plan.
                                      {
                                        label: "Editar plan de pagos",
                                        onClick: () => setCapexPlanModal(f),
                                      },
                                    ]
                                  : [
                                      // Línea de activo sin plan: los 3 ítems.
                                      {
                                        label: "Modificar fecha de pago",
                                        onClick: () => setCapexFechaModal(f),
                                      },
                                      {
                                        label: "Agregar plan de pagos",
                                        onClick: () => setCapexPlanModal(f),
                                      },
                                      {
                                        label: "Confirmar pago ejecutado",
                                        onClick: () => setCapexPagadoModal(f),
                                      },
                                    ]
                              }
                            />
                          ) : esEntryEditable(f) ? (
                            <FaEdit
                              title="Editar"
                              style={{ cursor: "pointer", color: "#999", fontSize: "1em" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (f.tipo === "pipeline") {
                                  setEntryEditarFecha(f);
                                } else if (f.tipo === "facturado") {
                                  setPlanCuotasModal({ factura: f, tipo: "cobro", accionInicial: "cuotas" });
                                } else if (f.tipo === "proveedor") {
                                  // Externas y SII recibidas reales → planificar fecha de pago.
                                  // Recurrentes/residuos no llegan acá: no renderizan ícono editable.
                                  setPlanCuotasModal({ factura: f, tipo: "pago", accionInicial: "fecha" });
                                } else if (f.tipo === "edificio") {
                                  setPagoEdificioModal(f.edificioData);
                                }
                              }}
                            />
                          ) : esFilaOverrideF29(f) && RUT_EMPRESA_POR_PAIS[pais] ? (
                            <FaEdit
                              title="Cargar valor del contador"
                              style={{ cursor: "pointer", color: "#999", fontSize: "1em" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOverrideF29Modal({
                                  entry: f,
                                  campo: f.id.startsWith("ppm") ? "ppm" : "iut",
                                  periodo: periodoDeImpuesto(f),
                                });
                              }}
                            />
                          ) : (
                            <span style={{ color: "#ccc", fontSize: "0.85rem" }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {planCuotasModal && (
        <PlanCuotasModal
          isOpen={!!planCuotasModal}
          onClose={() => setPlanCuotasModal(null)}
          tipo={planCuotasModal.tipo}
          factura={planCuotasModal.factura}
          accionInicial={planCuotasModal.accionInicial || "fecha"}
          onSuccess={() => cargarDatosFuturos()}
        />
      )}

      {vincularModal && (
        <VincularMovimientoModal
          show={!!vincularModal}
          onClose={() => setVincularModal(null)}
          movimiento={vincularModal}
          onSuccess={handleVincularSuccess}
          onRefreshFuturos={cargarDatosFuturos}
        />
      )}

      {pagoEdificioModal && (
        <PlanPagoEdificioModal
          isOpen={!!pagoEdificioModal}
          onClose={() => setPagoEdificioModal(null)}
          edificio={pagoEdificioModal}
          pais={pais}
          userId={userId}
          onSuccess={() => {
            setPagoEdificioModal(null);
            cargarDatosFuturos();
          }}
        />
      )}

      {entryEditarFecha && (
        <EditarFechaCobroModal
          show={!!entryEditarFecha}
          onHide={() => setEntryEditarFecha(null)}
          entry={entryEditarFecha}
          onSaved={() => {
            setEntryEditarFecha(null);
            cargarDatosFuturos();
          }}
        />
      )}

      {capexPagadoModal && (
        <ConfirmarCapexPagadoModal
          show={!!capexPagadoModal}
          onHide={() => setCapexPagadoModal(null)}
          entry={capexPagadoModal}
          onConfirm={handleMarcarCapexPagado}
        />
      )}

      {capexFechaModal && (
        <ModificarFechaProyeccionCapexModal
          show={!!capexFechaModal}
          onHide={() => setCapexFechaModal(null)}
          entry={capexFechaModal}
          onSaved={() => {
            setCapexFechaModal(null);
            cargarDatosFuturos();
          }}
        />
      )}

      {capexPlanModal && (
        <PlanPagosActivoModal
          show={!!capexPlanModal}
          onHide={() => setCapexPlanModal(null)}
          entry={capexPlanModal}
          userId={userId}
          onSaved={() => {
            setCapexPlanModal(null);
            // La proyección aún muestra la línea única (la bifurcación por cuotas es el commit 3);
            // igual se refresca por consistencia con los otros modales del menú.
            cargarDatosFuturos();
          }}
        />
      )}

      {overrideF29Modal && (
        <OverrideF29Modal
          show={!!overrideF29Modal}
          onHide={() => setOverrideF29Modal(null)}
          entry={overrideF29Modal.entry}
          campo={overrideF29Modal.campo}
          periodo={overrideF29Modal.periodo}
          onSave={handleGuardarOverrideF29}
        />
      )}
    </div>
  );
};

export default FlujoCajaV2;
